<template>
  <div>
    <b-overlay :show="formShow" rounded="sm" no-fade>
      <b-card class="m-0 p-0">
        <b-row class="mb-25">
          <b-col cols="12" class="mb-1 mb-md-0">
            <div class="date-container d-flex flex-column">
              <b-row class="d-flex align-items-center justify-content-between">
                <b-col
                  cols="12"
                  sm="12"
                  md="4"
                  class="d-flex align-items-center"
                >
                  <h4 class="mb-0">Action Plan Tool</h4>
                  <b-button
                    v-if="$Can('show_report_menu')"
                    :id="'report-control'"
                    variant="primary"
                    size="sm"
                    class="animate__animated animate__fadeIn ml-1"
                  >
                    <span class="d-flex align-items-center">
                      Reports
                      <feather-icon
                        icon="BarChart2Icon"
                        style="margin-left: 0.3rem;"
                      />
                    </span>
                  </b-button>
                  <b-popover
                    :target="'report-control'"
                    triggers="click blur"
                    variant="primary"
                    custom-class="report-popover-custom"
                  >
                    <template #title>
                      {{ "Report Control" }}
                    </template>
                    <div class="report-menu-container">
                      <b-button
                        v-if="$Can('show_start_year_report')"
                        variant="outline-primary"
                        size="sm"
                        @click="showReportModal('start')"
                      >
                        <span class="trades-font">
                          {{ "CIB Start of the Year Report" }}
                          <feather-icon
                            icon="PieChartIcon"
                            style="margin-left: 0.3rem;"
                          />
                        </span>
                      </b-button>

                      <b-button
                        v-if="$Can('show_middle_year_report')"
                        variant="outline-primary"
                        size="sm"
                        @click="showReportModal('mid')"
                      >
                        <span class="trades-font">
                          {{ "CIB Middle of the Year Report" }}
                          <feather-icon
                            icon="PieChartIcon"
                            style="margin-left: 0.3rem;"
                          />
                        </span>
                      </b-button>

                      <b-button
                        v-if="$Can('show_end_year_report')"
                        variant="outline-primary"
                        size="sm"
                        @click="showReportModal('end')"
                      >
                        <span class="trades-font">
                          {{ "CIB End of the Year Report" }}
                          <feather-icon
                            icon="PieChartIcon"
                            style="margin-left: 0.3rem;"
                          />
                        </span>
                      </b-button>

                      <b-button
                        v-if="$Can('show_adhoc_report')"
                        variant="outline-primary"
                        size="sm"
                        @click="showReportModal('adhoc')"
                      >
                        <span class="trades-font">
                          {{ "CIS Regional Company Action Plan Report" }}
                          <feather-icon
                            icon="FileTextIcon"
                            style="margin-left: 0.3rem;"
                          />
                        </span>
                      </b-button>

                      <b-button
                        v-if="$Can('show_national_report')"
                        variant="outline-primary"
                        size="sm"
                        @click="showReportModal('national')"
                      >
                        <span class="trades-font">
                          {{ "CIS National Progress Report" }}
                          <feather-icon
                            icon="TrendingUpIcon"
                            style="margin-left: 0.3rem;"
                          />
                        </span>
                      </b-button>
                    </div>
                  </b-popover>
                </b-col>
                <b-col
                  v-if="$Can('tool_filter_options')"
                  cols="12"
                  md="8"
                  class="d-flex align-items-center justify-content-end"
                >
                  <!-- priority year select -->
                  <v-select
                    v-model="priorityYear"
                    :options="priorityYearOptions"
                    placeholder="Select Year"
                    :reduce="(val) => val"
                    :clearable="true"
                    :searchable="false"
                    appendTo="body"
                    class="flex-1 small-select ml-2"
                    @input="onPriorityYearChange()"
                  />
                  <b-form-checkbox
                    v-model="showCurrentYearOnly"
                    switch
                    size="sm"
                    class="custom-switch ml-1"
                    @change="onlyShowCurrentYear"
                  >
                    <span class="switch-label ml-0 pl-0"
                      >Only show current year priorities</span
                    >
                  </b-form-checkbox>

                  <b-form-checkbox
                    v-model="showPreviousYear"
                    switch
                    size="sm"
                    class="custom-switch ml-1"
                    @change="onPreviousYearChange"
                  >
                    <span class="switch-label ml-0 pl-0"
                      >Include unfinished from previous years</span
                    >
                  </b-form-checkbox>

                  <!-- <b-button
                    variant="primary"
                    @click="downloadPDF"
                    class="mr-1 btn-sm"
                  >
                    Download PDF
                  </b-button> -->
                </b-col>
              </b-row>
            </div>
          </b-col>
        </b-row>

        <div
          class="table-responsive mt-2"
          v-if="$Can('show_tool_table')"
          @contextmenu.prevent="$refs.contextMenu.open($event)"
        >
          <table class="table table-bordered" v-if="groupedRows.length > 0">
            <thead>
              <tr style="font-size: 0.5rem!important;">
                <th
                  style="width: 30px; min-width: 30px; max-width: 30px;"
                  v-if="showDraggableHandle"
                ></th>
                <th v-if="isAdmin" class="cib-report-header"></th>
                <th class="objective-header">Name Of Objective</th>
                <th class="actions-header">Actions</th>
                <th class="national-column">National/Regional</th>
                <th>Timeline</th>
                <th>Year</th>
                <th class="owner-column">Owner</th>
                <th class="kpi-column">KPI</th>
                <th class="progress-column">Progress %</th>
                <th class="ontrack-column">On Track</th>
                <th>On Target</th>
                <th
                  style="width: 250px; min-width: 250px; max-width: 250px;white-space: wrap !important;font-size: 0.6rem !important"
                >
                  Critical Success Factors / Barriers to implementation
                </th>
                <th style="width: 250px; min-width: 250px; max-width: 250px;">
                  Additional Comments
                </th>
                <th
                  style="width: 60px; min-width: 60px; max-width: 60px;background-color: #008bcc!important;"
                >
                  #
                </th>
              </tr>
            </thead>
            <template v-for="(pillarGroup, pillarIndex) in groupedRows">
              <!-- Pillar Row -->
              <tr :key="'pillar-' + pillarIndex">
                <td
                  :colspan="isAdmin ? 14 : 13"
                  class="pillar-cell"
                  @click="toggleSelect($event, 'pillar', pillarIndex)"
                  :class="{ editing: isEditing(pillarIndex, 'pillar') }"
                >
                  <div
                    class="d-flex align-items-center justify-content-between w-100"
                  >
                    <template v-if="isEditing(pillarIndex, 'pillar')">
                      <v-select
                        v-model="pillarGroup.pillar_id"
                        placeholder="Select Pillar"
                        :options="getFilteredPillarOptions()"
                        label="title"
                        :reduce="(val) => val.id"
                        class="fixed-select pillar-select"
                        appendTo="body"
                        @blur="hideSelect($event, 'pillar', pillarIndex)"
                        @input="onSelectChange($event, 'pillar', pillarIndex)"
                        @option:created="
                          onCreateNewField($event, 'pillar', null, pillarIndex)
                        "
                        :taggable="true"
                        :selectable="(option) => !option.disabled"
                        :loading="isOnSelectChangeLoading"
                      />
                    </template>
                    <template v-else>
                      <div
                        class="d-flex align-items-center flex-grow-1"
                        :style="
                          'background-color:' +
                            pillarGroup.color +
                            ' !important'
                        "
                      >
                        <span class="cell-text pillar-text">
                          {{
                            pillarGroup.pillar_id
                              ? "Pillar-" +
                                pillarGroup.order +
                                ": " +
                                getOptionTitle(
                                  pillarGroup.pillar_id,
                                  "pillar_id"
                                )
                              : "Select Pillar"
                          }}
                        </span>
                        <div class="pillar-action-column">
                          <b-button
                            v-if="
                              !pillarGroup.pillar_titles ||
                                pillarGroup.pillar_titles.length === 0
                            "
                            variant="outline-danger"
                            size="sm"
                            @click="deletePillar(pillarIndex)"
                            class="delete-pillar-btn"
                            v-b-tooltip.hover
                            title="Delete Pillar"
                          >
                            Delete Pillar <i class="fas fa-times ml-1"></i>
                          </b-button>
                        </div>
                      </div>
                    </template>
                  </div>
                </td>
              </tr>

              <!-- Pillar Titles-->
              <template
                v-for="(pillarTitle, titleIndex) in pillarGroup.pillar_titles"
              >
                <!-- Pillar Title Row -->
                <tr :key="'pillar-title-' + pillarIndex + '-' + titleIndex">
                  <td
                    :colspan="isAdmin ? 13 : 12"
                    @click="
                      toggleSelect(
                        $event,
                        'pillarTitle',
                        pillarIndex + '-' + titleIndex
                      )
                    "
                    :class="{
                      editing: isEditing(
                        pillarIndex + '-' + titleIndex,
                        'pillarTitle'
                      ),
                    }"
                    class="pillar-cell"
                  >
                    <div
                      class="d-flex align-items-center justify-content-between w-100"
                    >
                      <template
                        v-if="
                          isEditing(
                            pillarIndex + '-' + titleIndex,
                            'pillarTitle'
                          )
                        "
                      >
                        <v-select
                          v-model="pillarTitle.pillar_title_id"
                          :options="
                            getFilteredPillarTitles(pillarGroup.pillar_id)
                          "
                          placeholder="Select Pillar Title"
                          label="title"
                          :reduce="(val) => val.id"
                          class="fixed-select pillar-select"
                          appendTo="body"
                          @blur="
                            hideSelect(
                              $event,
                              'pillarTitle',
                              pillarIndex + '-' + titleIndex
                            )
                          "
                          @input="
                            onSelectChange(
                              $event,
                              'pillarTitle',
                              pillarIndex + '-' + titleIndex
                            )
                          "
                          :selectable="(option) => !option.disabled"
                        />
                      </template>
                      <template v-else>
                        <div
                          class="d-flex align-items-center flex-grow-1"
                          :style="
                            'background-color:' +
                              pillarGroup.color +
                              ' !important'
                          "
                        >
                          <span class="cell-text pillar-title-text">
                            {{
                              pillarTitle.pillar_title_id
                                ? getOptionTitle(
                                    pillarTitle.pillar_title_id,
                                    "pillar_title_id"
                                  )
                                : "Select Pillar Title"
                            }}
                          </span>
                        </div>
                      </template>
                    </div>
                  </td>
                  <!-- Add Pillar Title -->
                  <td class="text-right add-row-cell sticky-pillar-title-row">
                    <div class="d-flex align-items-center justify-content-end">
                      <b-button
                        v-if="$Can('add_tool_item')"
                        variant="success"
                        size="sm"
                        @click="addPillarTitle(pillarIndex)"
                        class="pillar-title-action-btn"
                        :disabled="
                          pillarGroup.pillar_titles.length ==
                            getFilteredPillarTitles(pillarGroup.pillar_id)
                              .length
                        "
                      >
                        Add Pillar Title <i class="fas fa-plus ml-1"></i>
                      </b-button>

                      <b-button
                        v-if="$Can('delete_tool_item')"
                        variant="danger"
                        size="sm"
                        @click="deletePillarTitle(pillarIndex, titleIndex)"
                        class="pillar-title-action-btn"
                      >
                        Delete Pillar Title
                        <i class="fas fa-trash  ml-1"></i>
                      </b-button>
                    </div>
                  </td>
                </tr>

                <!-- Rows for this Pillar Title -->
                <draggable
                  :list="pillarTitle.rows"
                  tag="tbody"
                  @end="onDragEnd($event, pillarIndex, titleIndex)"
                  handle=".drag-handle, .row-actions-drag-handle"
                  :animation="200"
                  ghost-class="ghost-row"
                  drag-class="drag-row"
                >
                  <tr
                    v-for="(row, rowIndex) in pillarTitle.rows"
                    :key="
                      'details-' +
                        pillarIndex +
                        '-' +
                        titleIndex +
                        '-' +
                        rowIndex
                    "
                    :class="{ 'cib-report-selected': row.cib_report }"
                  >
                    <!-- Drag handle column -->
                    <td class="drag-handle-cell" v-if="showDraggableHandle">
                      <i class="fas fa-grip-vertical drag-handle"></i>
                    </td>

                    <!-- CIB Report CheckBox -->
                    <td
                      v-if="isAdmin"
                      :class="{
                        editing: isEditing(
                          pillarIndex + '-' + titleIndex + '-' + rowIndex,
                          'cib_report'
                        ),
                        'cib-report-cell': true,
                      }"
                      style="width: 50px !important; min-width: 50px !important; max-width: 50px !important; text-align: center;"
                    >
                      <div class="cib-checkbox-container">
                        <span v-if="row.cib_report" class="cib-text">CIB</span>
                        <b-form-checkbox
                          v-model="row.cib_report"
                          class="custom-control-primary mx-auto"
                          :class="{ 'hover-visible': row.cib_report }"
                          @change="onCibReportChange(row)"
                        />
                      </div>
                    </td>
                    <!-- Objective NAME OF OBJECTIVE -->
                    <td
                      @click="
                        toggleSelect(
                          $event,
                          'objective',
                          pillarIndex + '-' + titleIndex + '-' + rowIndex
                        )
                      "
                      :class="{
                        editing: isEditing(
                          pillarIndex + '-' + titleIndex + '-' + rowIndex,
                          'objective'
                        ),
                        'objective-span-cell': true,
                      }"
                    >
                      <template
                        v-if="
                          isEditing(
                            pillarIndex + '-' + titleIndex + '-' + rowIndex,
                            'objective'
                          )
                        "
                      >
                        <v-select
                          v-model="row.objective_id"
                          :options="
                            getFilteredObjectives(
                              pillarTitle.pillar_title_id,
                              row.objective_id
                            )
                          "
                          placeholder="Select Objective"
                          label="title"
                          :reduce="(val) => val.id"
                          :clearable="true"
                          class="fixed-select objective-select"
                          appendTo="body"
                          :taggable="true"
                          @option:created="
                            onCreateNewField(
                              $event,
                              'objective',
                              pillarTitle.pillar_title_id,
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          @blur="
                            hideSelect(
                              $event,
                              'objective',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          @input="
                            onSelectChange(
                              $event,
                              'objective',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                        />
                      </template>
                      <template v-else>
                        <span
                          :id="
                            `popover-objective-${pillarIndex}-${titleIndex}-${rowIndex}`
                          "
                          class="cell-text"
                          :class="{ placeholder: !row.objective_id }"
                        >
                          {{
                            row.objective_id
                              ? getOptionTitle(row.objective_id, "objective_id")
                              : "Select Objective"
                          }}
                        </span>
                        <b-popover
                          v-if="row.objective_id"
                          :delay="1000"
                          :target="
                            `popover-objective-${pillarIndex}-${titleIndex}-${rowIndex}`
                          "
                          triggers="hover"
                        >
                          <template #title>
                            <span>Name Of Objective</span>
                          </template>
                          <span>{{
                            getOptionTitle(row.objective_id, "objective_id")
                          }}</span>
                        </b-popover>
                      </template>
                    </td>
                    <!-- Actions -->
                    <td
                      @click="
                        toggleSelect(
                          $event,
                          'actions',
                          pillarIndex + '-' + titleIndex + '-' + rowIndex
                        )
                      "
                      :class="{
                        editing: isEditing(
                          pillarIndex + '-' + titleIndex + '-' + rowIndex,
                          'actions'
                        ),
                        'actions-span-cell': true,
                      }"
                    >
                      <template
                        v-if="
                          isEditing(
                            pillarIndex + '-' + titleIndex + '-' + rowIndex,
                            'actions'
                          )
                        "
                      >
                        <v-select
                          v-model="row.action_id"
                          :options="
                            getFilteredActions(
                              row.objective_id,
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          placeholder="Select Actions"
                          label="title"
                          :reduce="(val) => val.id"
                          :clearable="true"
                          class="fixed-select actions-select"
                          appendTo="body"
                          :taggable="true"
                          @option:created="
                            onCreateNewField(
                              $event,
                              'action',
                              row.objective_id,
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          @blur="
                            hideSelect(
                              $event,
                              'actions',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          @input="
                            onSelectChange(
                              $event,
                              'action',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                        />
                      </template>
                      <template v-else>
                        <span
                          :id="
                            `popover-actions-${pillarIndex}-${titleIndex}-${rowIndex}`
                          "
                          class="cell-text"
                        >
                          {{
                            row.action_id
                              ? getOptionTitle(row.action_id, "action_id")
                              : "Select Actions"
                          }}
                        </span>
                        <b-popover
                          v-if="row.action_id"
                          :delay="1000"
                          :target="
                            `popover-actions-${pillarIndex}-${titleIndex}-${rowIndex}`
                          "
                          triggers="hover"
                        >
                          <template #title>
                            <span>Actions</span>
                          </template>
                          <div class="popover-content-list">
                            <template
                              v-for="(item, index) in formatBulletPoints(
                                getOptionTitle(row.action_id, 'action_id')
                              )"
                            >
                              <div :key="index" class="popover-list-item">
                                • {{ item }}
                              </div>
                            </template>
                          </div>
                        </b-popover>
                      </template>
                    </td>

                    <!-- National/Regional -->
                    <td
                      class="national-column"
                      @click="
                        toggleSelect(
                          $event,
                          'national',
                          pillarIndex + '-' + titleIndex + '-' + rowIndex
                        )
                      "
                      :class="{
                        editing: isEditing(
                          pillarIndex + '-' + titleIndex + '-' + rowIndex,
                          'national'
                        ),
                      }"
                    >
                      <template
                        v-if="
                          isEditing(
                            pillarIndex + '-' + titleIndex + '-' + rowIndex,
                            'national'
                          )
                        "
                      >
                        <v-select
                          v-model="row.national"
                          :options="['National', 'Regional']"
                          placeholder="Select National/Regional"
                          class="fixed-select"
                          appendTo="body"
                          :clearable="true"
                          @blur="
                            hideSelect(
                              $event,
                              'national',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          @input="
                            onSelectChange(
                              $event,
                              'national',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                        />
                      </template>
                      <template v-else>
                        <span class="cell-text">{{
                          row.national || "Select National/Regional"
                        }}</span>
                      </template>
                    </td>

                    <!-- Timeline -->
                    <td
                      @click="
                        toggleSelect(
                          $event,
                          'timeline',
                          pillarIndex + '-' + titleIndex + '-' + rowIndex
                        )
                      "
                      :class="{
                        editing: isEditing(
                          pillarIndex + '-' + titleIndex + '-' + rowIndex,
                          'timeline'
                        ),
                      }"
                      style="width: 85px !important; min-width: 85px !important; max-width: 85px !important;"
                    >
                      <template
                        v-if="
                          isEditing(
                            pillarIndex + '-' + titleIndex + '-' + rowIndex,
                            'timeline'
                          )
                        "
                      >
                        <v-select
                          v-model="row.timeline"
                          :options="timelineOptions"
                          placeholder="Select Timeline"
                          class="fixed-select fixed-timeline-select"
                          style="width: 70px !important; max-width: 70px !important; min-width: 70px !important;"
                          :clearable="true"
                          :searchable="false"
                          appendTo="body"
                          @option:selected="onTrackChange(row.onTrack, row)"
                          @blur="
                            hideSelect(
                              $event,
                              'timeline',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          @input="
                            onSelectChange(
                              $event,
                              'timeline',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                        />
                      </template>
                      <template v-else>
                        <span class="cell-text">{{
                          row.timeline ? row.timeline : "Select Timeline"
                        }}</span>
                      </template>
                    </td>

                    <!-- Year -->
                    <td
                      @click="
                        toggleSelect(
                          $event,
                          'year',
                          pillarIndex + '-' + titleIndex + '-' + rowIndex
                        )
                      "
                      :class="{
                        editing: isEditing(
                          pillarIndex + '-' + titleIndex + '-' + rowIndex,
                          'year'
                        ),
                      }"
                      style="width: 85px !important; min-width: 85px !important; max-width: 85px !important;"
                    >
                      <template
                        v-if="
                          isEditing(
                            pillarIndex + '-' + titleIndex + '-' + rowIndex,
                            'year'
                          )
                        "
                      >
                        <v-select
                          v-model="row.year"
                          :options="yearOptions"
                          placeholder="Select Year"
                          :reduce="(val) => val"
                          class="fixed-select fixed-year-select"
                          :clearable="false"
                          :searchable="false"
                          appendTo="body"
                          @blur="
                            hideSelect(
                              $event,
                              'year',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          @input="
                            onSelectChange(
                              $event,
                              'year',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                        />
                      </template>
                      <template v-else>
                        <span class="cell-text">{{ row.year }}</span>
                      </template>
                    </td>

                    <!-- Owner -->
                    <td
                      class="owner-column"
                      @click="
                        toggleSelect(
                          $event,
                          'owner',
                          pillarIndex + '-' + titleIndex + '-' + rowIndex
                        )
                      "
                      :class="{
                        editing: isEditing(
                          pillarIndex + '-' + titleIndex + '-' + rowIndex,
                          'owner'
                        ),
                      }"
                    >
                      <template
                        v-if="
                          isEditing(
                            pillarIndex + '-' + titleIndex + '-' + rowIndex,
                            'owner'
                          )
                        "
                      >
                        <treeselect
                          v-model="row.owner_selection"
                          :multiple="true"
                          :options="transformedOwnerOptions"
                          :normalizer="ownerNormalizer"
                          placeholder="Select Owner"
                          :flat="false"
                          :clearable="false"
                          :disable-branch-nodes="true"
                          :value-consists-of="
                            row.owner_id === crossRegionalOwnerId
                              ? 'LEAF_PRIORITY'
                              : 'ALL'
                          "
                          :limit="0"
                          :limit-text="(count) => `${count} owners selected`"
                          @input="
                            handleOwnerSelection(
                              $event,
                              row,
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                        >
                          <template #option-label="{ node }">
                            <span :title="node.label" class="truncate-text">{{
                              node.label
                            }}</span>
                          </template>
                          <template #value-label="{ node }">
                            <span class="truncate-text">
                              {{
                                node.raw.isWorkingGroup
                                  ? node.label
                                  : node.label
                              }}
                            </span>
                          </template>
                        </treeselect>
                      </template>
                      <template v-else>
                        <span
                          :id="
                            `popover-owner-${pillarIndex}-${titleIndex}-${rowIndex}`
                          "
                          class="cell-text owner-text"
                        >
                          {{ getOwnerDisplayText(row) }}
                          <b-badge
                            v-if="row.wg_ids && row.wg_ids.length > 0"
                            variant="light-primary"
                            class="wg-badge ml-1"
                            pill
                          >
                            WG
                          </b-badge>
                        </span>
                        <b-popover
                          v-if="row.wg_ids && row.wg_ids.length > 0"
                          :target="
                            `popover-owner-${pillarIndex}-${titleIndex}-${rowIndex}`
                          "
                          triggers="hover"
                          :delay="1000"
                        >
                          <template #title>
                            <span>Owner</span>
                          </template>
                          <span>{{ getOwnerDisplayText(row) }}</span>
                        </b-popover>
                      </template>
                    </td>

                    <!-- KPI -->
                    <td
                      class="kpi-column"
                      @click="
                        toggleSelect(
                          $event,
                          'kpi',
                          pillarIndex + '-' + titleIndex + '-' + rowIndex
                        )
                      "
                      :class="{
                        editing: isEditing(
                          pillarIndex + '-' + titleIndex + '-' + rowIndex,
                          'kpi'
                        ),
                      }"
                    >
                      <template
                        v-if="
                          isEditing(
                            pillarIndex + '-' + titleIndex + '-' + rowIndex,
                            'kpi'
                          )
                        "
                      >
                        <v-select
                          v-model="row.kpi_id"
                          :options="getFilteredKPIs(row.action_id)"
                          placeholder="Select KPI"
                          label="title"
                          :reduce="(val) => val.id"
                          class="fixed-select"
                          appendTo="body"
                          :taggable="true"
                          @option:created="
                            onCreateNewField(
                              { title: $event },
                              'kpi',
                              row.action_id,
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          @blur="
                            hideSelect(
                              $event,
                              'kpi',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          @input="
                            onSelectChange(
                              $event,
                              'kpi',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                        />
                      </template>
                      <template v-else>
                        <span
                          :id="
                            `popover-kpi-${pillarIndex}-${titleIndex}-${rowIndex}`
                          "
                          class="cell-text"
                        >
                          {{
                            row.kpi_id
                              ? getOptionTitle(row.kpi_id, "kpi_id")
                              : "Select KPI"
                          }}
                        </span>
                        <b-popover
                          v-if="row.kpi_id"
                          :target="
                            `popover-kpi-${pillarIndex}-${titleIndex}-${rowIndex}`
                          "
                          triggers="hover"
                          :delay="1000"
                        >
                          <template #title>
                            <span>KPI</span>
                          </template>
                          <div class="popover-content-list">
                            <template
                              v-for="(item, index) in formatBulletPoints(
                                getOptionTitle(row.kpi_id, 'kpi_id')
                              )"
                            >
                              <div :key="index" class="popover-list-item">
                                • {{ item }}
                              </div>
                            </template>
                          </div>
                        </b-popover>
                      </template>
                    </td>

                    <!-- Progress -->
                    <td
                      class="progress-column"
                      @click="
                        toggleSelect(
                          $event,
                          'progress',
                          pillarIndex + '-' + titleIndex + '-' + rowIndex
                        )
                      "
                      :class="{
                        editing: isEditing(
                          pillarIndex + '-' + titleIndex + '-' + rowIndex,
                          'progress'
                        ),
                      }"
                    >
                      <template
                        v-if="
                          isEditing(
                            pillarIndex + '-' + titleIndex + '-' + rowIndex,
                            'progress'
                          )
                        "
                      >
                        <input
                          type="number"
                          placeholder="Enter Progress"
                          v-model.number="row.progress"
                          class="form-control progress-input"
                          min="0"
                          max="100"
                          @change="validateProgress($event, row)"
                          @blur="
                            hideSelect(
                              $event,
                              'progress',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                        />
                      </template>
                      <template v-else>
                        <span class="cell-text"
                          >{{ row.progress || "0" }}%</span
                        >
                      </template>
                    </td>

                    <!-- On Track -->
                    <td
                      class="ontrack-column"
                      @click="
                        toggleSelect(
                          $event,
                          'onTrack',
                          pillarIndex + '-' + titleIndex + '-' + rowIndex
                        )
                      "
                      :class="{
                        editing: isEditing(
                          pillarIndex + '-' + titleIndex + '-' + rowIndex,
                          'onTrack'
                        ),
                      }"
                    >
                      <template
                        v-if="
                          isEditing(
                            pillarIndex + '-' + titleIndex + '-' + rowIndex,
                            'onTrack'
                          )
                        "
                      >
                        <v-select
                          v-model="row.onTrack"
                          placeholder="Select On Track"
                          :options="['No', 'Yes']"
                          class="fixed-select"
                          appendTo="body"
                          @option:selected="onTrackChange($event, row)"
                          @blur="
                            hideSelect(
                              $event,
                              'onTrack',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          @input="
                            onSelectChange(
                              $event,
                              'onTrack',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                        />
                      </template>
                      <template v-else>
                        <span class="cell-text">{{
                          row.onTrack || "Select On Track"
                        }}</span>
                      </template>
                    </td>

                    <!-- On Target -->
                    <td>
                      <div class="progress">
                        <div
                          class="progress-bar"
                          :style="{
                            width: row.progress + '%',
                            backgroundColor: getProgressColor(row.progress),
                          }"
                        >
                          {{ row.progress }}%
                        </div>
                      </div>
                    </td>

                    <!-- CSF comment -->
                    <td
                      @click="
                        toggleSelect(
                          $event,
                          'csf',
                          pillarIndex + '-' + titleIndex + '-' + rowIndex
                        )
                      "
                      :class="{
                        editing: isEditing(
                          pillarIndex + '-' + titleIndex + '-' + rowIndex,
                          'csf'
                        ),
                        'csf-cell': true,
                      }"
                    >
                      <template
                        v-if="
                          isEditing(
                            pillarIndex + '-' + titleIndex + '-' + rowIndex,
                            'csf'
                          )
                        "
                      >
                        <textarea
                          v-model="row.csf_comment"
                          class="form-control csf-comment-input"
                          placeholder="Enter critical success factors"
                          rows="2"
                          @blur="
                            hideSelect(
                              $event,
                              'csf',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          @change="
                            onTextareaChange(
                              $event,
                              'csf',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                        ></textarea>
                      </template>
                      <template v-else>
                        <span
                          :id="
                            `popover-csf-${pillarIndex}-${titleIndex}-${rowIndex}`
                          "
                          class="cell-text"
                        >
                          {{
                            row.csf_comment || "Enter critical success factors"
                          }}
                        </span>
                        <b-popover
                          v-if="row.csf_comment"
                          :target="
                            `popover-csf-${pillarIndex}-${titleIndex}-${rowIndex}`
                          "
                          triggers="hover"
                          :delay="1000"
                        >
                          <template #title>
                            <span>Critical Success Factors</span>
                          </template>
                          <span>{{ row.csf_comment }}</span>
                        </b-popover>
                      </template>
                    </td>

                    <!-- additional comment / Narrative -->
                    <td
                      @click="
                        toggleSelect(
                          $event,
                          'additionalComment',
                          pillarIndex + '-' + titleIndex + '-' + rowIndex
                        )
                      "
                      :class="{
                        editing: isEditing(
                          pillarIndex + '-' + titleIndex + '-' + rowIndex,
                          'additionalComment'
                        ),
                        'additional-comment-cell': true,
                      }"
                    >
                      <template
                        v-if="
                          isEditing(
                            pillarIndex + '-' + titleIndex + '-' + rowIndex,
                            'additionalComment'
                          )
                        "
                      >
                        <textarea
                          v-model="row.additional_comment"
                          class="form-control additional-comment-input"
                          placeholder="Enter additional comments"
                          rows="2"
                          @blur="
                            hideSelect(
                              $event,
                              'additionalComment',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                          @change="
                            onTextareaChange(
                              $event,
                              'additionalComment',
                              pillarIndex + '-' + titleIndex + '-' + rowIndex
                            )
                          "
                        ></textarea>
                      </template>
                      <template v-else>
                        <span
                          :id="
                            `popover-additional-${pillarIndex}-${titleIndex}-${rowIndex}`
                          "
                          class="cell-text"
                        >
                          {{
                            row.additional_comment ||
                              "Enter additional comments"
                          }}
                        </span>
                        <b-popover
                          v-if="row.additional_comment"
                          :target="
                            `popover-additional-${pillarIndex}-${titleIndex}-${rowIndex}`
                          "
                          triggers="hover"
                          :delay="1000"
                        >
                          <template #title>
                            <span>Additional Comments</span>
                          </template>
                          <span>{{ row.additional_comment }}</span>
                        </b-popover>
                      </template>
                    </td>

                    <!-- Sticky menu Button -->
                    <td
                      class="action-column sticky-action-column"
                      style="width: 60px; min-width: 60px; max-width: 60px;     height: 60px !important;
          background-color: rgba(255, 255, 255, 0.4) !important;"
                    >
                      <b-button
                        :id="
                          'row-controls-' +
                            pillarIndex +
                            '-' +
                            titleIndex +
                            '-' +
                            rowIndex
                        "
                        variant="flat-primary"
                        size="sm"
                        class="top-btns animate__animated animate__fadeIn"
                      >
                        <i class="fa-solid fa-bars row-actions-drag-handle"></i>
                      </b-button>
                      <b-popover
                        :target="
                          'row-controls-' +
                            pillarIndex +
                            '-' +
                            titleIndex +
                            '-' +
                            rowIndex
                        "
                        triggers="click blur"
                        variant="primary"
                      >
                        <template #title>
                          {{ "Row Controls" }}
                        </template>

                        <b-button
                          v-if="$Can('delete_tool_item')"
                          variant="outline-danger"
                          size="sm"
                          @click="deleteRow(pillarIndex, titleIndex, rowIndex)"
                          class="action-row-btn"
                          v-b-tooltip.hover
                          title="Delete Row"
                        >
                          <span class="trades-font">
                            {{ "Delete Row" }}
                            <feather-icon
                              icon="TrashIcon"
                              style="margin-left: 0.3rem;"
                            />
                          </span>
                        </b-button>
                      </b-popover>
                    </td>
                  </tr>
                </draggable>

                <!-- Add Row Button for this Pillar Title -->
                <tr :key="'add-row-' + pillarIndex + '-' + titleIndex">
                  <td :colspan="isAdmin ? 12 : 12"></td>
                  <td class="text-right add-row-cell sticky-add-row">
                    <b-button
                      v-if="$Can('add_tool_item')"
                      variant="outline-primary"
                      size="sm"
                      @click="addRowToPillarTitle(pillarIndex, titleIndex)"
                      class="add-row-btn"
                    >
                      Add Row <i class="fas fa-plus ml-1"></i>
                    </b-button>
                  </td>
                </tr>
              </template>
            </template>
          </table>
          <b-alert variant="primary" show v-else>
            <div class="alert-body">
              <span
                ><strong style="display: block;"
                  >No Tool Data Found</strong
                ></span
              >
            </div>
          </b-alert>
        </div>
        <b-alert variant="primary" show v-else>
          <div class="alert-body">
            <span
              ><strong style="display: block;"
                >You are not authorized to view this tool</strong
              ></span
            >
          </div>
        </b-alert>

        <b-button
          v-if="$Can('add_tool_item')"
          variant="success"
          @click="addNewPillar"
          class="mt-2"
          :disabled="disableNewPillarButton"
          >Add New Pillar</b-button
        >
      </b-card>
    </b-overlay>

    <!-- Report Modal -->
    <b-modal
      v-model="showReportModalFlag"
      :title="getReportModalTitle"
      size="lg"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="report-modal-content">
        <!-- Start of Year Report Options -->
        <template v-if="selectedReportType === 'start'">
          <b-form-group label="Select Year for Action Plan">
            <v-select
              v-model="reportOptions.year"
              :options="yearOptions"
              placeholder="Select Year"
              :clearable="false"
            />
          </b-form-group>
          <b-form-group label="Service Delivery Company Board Chairperson ">
            <input
              type="text"
              class="form-control"
              v-model="reportOptions.sdcb_chairperson"
            />
          </b-form-group>
        </template>

        <!-- Mid Year Report Options -->
        <template v-if="selectedReportType === 'mid'">
          <b-form-group label="Select Year">
            <v-select
              v-model="reportOptions.year"
              :options="yearOptions"
              placeholder="Select Year"
              :clearable="false"
            />
          </b-form-group>
          <b-form-group label="Service Delivery Company Board Chairperson ">
            <input
              type="text"
              class="form-control"
              v-model="reportOptions.sdcb_chairperson"
            />
          </b-form-group>
        </template>

        <!-- Year End Report Options -->
        <template v-if="selectedReportType === 'end'">
          <b-form-group label="Select Year">
            <v-select
              v-model="reportOptions.year"
              :options="yearOptions"
              placeholder="Select Year"
              :clearable="false"
            />
          </b-form-group>
          <b-form-group label="Service Delivery Company Board Chairperson ">
            <input
              type="text"
              class="form-control"
              v-model="reportOptions.sdcb_chairperson"
            />
          </b-form-group>
        </template>

        <!-- Adhoc Report Options -->
        <template v-if="selectedReportType === 'adhoc'">
          <b-form-group label="Select Columns">
            <v-select
              v-model="reportOptions.columns"
              :options="availableColumns"
              placeholder="Select Columns"
              multiple
              :clearable="true"
              :close-on-select="false"
            />
          </b-form-group>
          <b-form-group label="Filter by Pillar (optional)">
            <v-select
              v-model="reportOptions.pillarId"
              :options="pillarOptions"
              label="title"
              :reduce="val => val.id"
              placeholder="All pillars"
              :clearable="true"
            />
          </b-form-group>
          <b-form-group label="Select Year">
            <v-select
              v-model="reportOptions.year"
              :options="yearOptions"
              placeholder="Select Year"
              :clearable="false"
            />
          </b-form-group>
          <b-form-group label="Service Delivery Company Board Chairperson ">
            <input
              type="text"
              class="form-control"
              v-model="reportOptions.sdcb_chairperson"
            />
          </b-form-group>
          <b-form-group
            label="Select Report Type"
            v-if="$Can('show_report_type')"
          >
            <b-form-checkbox-group
              v-model="reportOptions.report_detail"
              class="d-flex align-items-center"
            >
              <b-form-checkbox value="National">
                National
              </b-form-checkbox>
              <b-form-checkbox value="Regional" class="ml-1">
                Regional
              </b-form-checkbox>
            </b-form-checkbox-group>
          </b-form-group>

          <!-- <b-form-group
            label="Select Company"
            v-if="
              reportOptions.report_detail === 'national' &&
                $Can('show_report_type')
            "
          >
            <v-select
              v-model="reportOptions.companies"
              :options="companiesOptions"
              placeholder="Select Company"
              :reduce="(val) => val.id"
              label="title"
              multiple
              :clearable="true"
              :close-on-select="false"
            />
          </b-form-group> -->
        </template>

        <!-- National Report Options -->
        <template v-if="selectedReportType === 'national'">
          <b-form-group label="Select Columns">
            <v-select
              v-model="reportOptions.columns"
              :options="
                availableColumns.filter((c) => c.label !== 'National/Regional')
              "
              placeholder="Select Columns"
              multiple
              :clearable="true"
              :close-on-select="false"
            />
          </b-form-group>
          <b-form-group label="Filter by Pillar (optional)">
            <v-select
              v-model="reportOptions.pillarId"
              :options="pillarOptions"
              label="title"
              :reduce="val => val.id"
              placeholder="All pillars"
              :clearable="true"
            />
          </b-form-group>
          <b-form-group label="Select Year">
            <v-select
              v-model="reportOptions.year"
              :options="yearOptions"
              placeholder="Select Year"
              :clearable="false"
            />
          </b-form-group>
          <b-form-group label="Service Delivery Company Board Chairperson ">
            <input
              type="text"
              class="form-control"
              v-model="reportOptions.sdcb_chairperson"
            />
          </b-form-group>
          <!-- 
          <b-form-group
            label="Select Report Type"
            v-if="$Can('show_report_type')"
          >
            <div class="d-flex align-items-center">
              <b-form-radio
                v-model="reportOptions.report_detail"
                name="report_detail"
                value="national"
              >
                National
              </b-form-radio>
              <b-form-radio
                v-model="reportOptions.report_detail"
                name="report_detail"
                value="regional"
                class="ml-1"
                @change="
                  () => {
                    reportOptions.companies = [];
                  }
                "
              >
                Regional
              </b-form-radio>
            </div>
          </b-form-group> -->

          <!-- <b-form-group
            label="Select Company"
            v-if="
              reportOptions.report_detail === 'national' &&
                $Can('show_report_type')
            "
          >
            <v-select
              v-model="reportOptions.companies"
              :options="companiesOptions"
              placeholder="Select Company"
              :reduce="(val) => val.id"
              label="title"
              multiple
              :clearable="true"
              :close-on-select="false"
            />
          </b-form-group> -->
        </template>

        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="showReportModalFlag = false"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            @click="generateReport"
            :disabled="!isValidReportOptions"
          >
            Create Report
          </b-button>
        </div>
      </div>
    </b-modal>

    <vue-context ref="contextMenu">
      <li>
        <b-link
          class="d-flex align-items-center"
          @click="showDraggableHandle = !showDraggableHandle"
        >
          <feather-icon
            :icon="showDraggableHandle ? 'EyeOffIcon' : 'EyeIcon'"
            size="16"
          />
          <span class="ml-75">{{
            showDraggableHandle
              ? "Hide Draggable Handle"
              : "Show Draggable Handle"
          }}</span>
        </b-link>
      </li>
    </vue-context>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BOverlay,
  VBPopover,
  BBadge,
  BTooltip,
  BFormGroup,
  BFormRadio,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import vSelect from "vue-select";
import toolsStoreModule from "./toolStoreModule";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import draggable from "vuedraggable";
import VueContext from "vue-context";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BOverlay,
    vSelect,
    ToastificationContent,
    BBadge,
    BTooltip,
    BFormGroup,
    BFormRadio,
    Treeselect,
    draggable,
    VueContext,
  },
  directives: {
    "b-popover": VBPopover,
  },

  setup() {
    const TOOL_APP_STORE_MODULE_NAME = "tools";
    if (!store.hasModule(TOOL_APP_STORE_MODULE_NAME))
      store.registerModule(TOOL_APP_STORE_MODULE_NAME, toolsStoreModule);

    onUnmounted(() => {
      if (store.hasModule(TOOL_APP_STORE_MODULE_NAME))
        store.unregisterModule(TOOL_APP_STORE_MODULE_NAME);
    });
  },

  data() {
    return {
      dateNow: new Date().toLocaleDateString(),
      formShow: false,
      showDraggableHandle: false,
      isOnSelectChangeLoading: false,
      showCurrentYearOnly: false,
      showPreviousYear: true,
      priorityYear: new Date().getFullYear().toString(),
      tableRows: [],
      pillarOptions: [],
      objectiveOptions: [],
      pillarTitleOptions: [],

      actionsOptions: [],
      ownerOptions: [],
      priorities: [],
      kpiOptions: [],
      timelineOptions: ["Q1", "Q2", "Q3", "Q4"],
      yearOptions: Array.from({ length: 5 }, (_, i) => (2024 + i).toString()),
      priorityYearOptions: Array.from({ length: 5 }, (_, i) =>
        (2024 + i).toString()
      ),
      toolOptions: [],
      companiesOptions: [],
      editingCell: {},

      userData: JSON.parse(localStorage.getItem("userData") || "{}"),
      isCreatingNewField: false,
      isCreatedField: false,
      workingGroupOptions: [],
      crossRegionalOwnerId: null,
      showReportModalFlag: false,
      selectedReportType: null,
      reportOptions: {
        year: new Date().getFullYear().toString(),
        sdcb_chairperson: null,
        report_detail: ["Regional", "National"],
        companies: [],
        columns: [
          { label: "Name Of Objective", value: "objective" },
          { label: "Actions", value: "actions" },
          { label: "KPI", value: "kpi" },
          { label: "Timeline", value: "timeline" },
        ],
      },
      availableColumns: [
        { label: "Name Of Objective", value: "objective" },
        { label: "Actions", value: "actions" },
        { label: "National/Regional", value: "national" },
        { label: "Timeline", value: "timeline" },
        { label: "Year", value: "year" },
        { label: "Owner", value: "owner" },
        { label: "KPI", value: "kpi" },
        { label: "Progress %", value: "progress" },
        // { label: "On Target", value: "onTarget" },
        { label: "On Track", value: "onTrack" },
        { label: "Critical Success Factors", value: "csf" },
        { label: "Additional Comments", value: "comments" },
      ],
    };
  },

  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    this.getToolOptions();

  },

  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },

  computed: {
    disableNewPillarButton() {
      return this.tableRows.length >= this.pillarOptions.length;
    },
    groupedRows() {
      return this.tableRows;
    },
    isAdmin() {
      return (
        this.userData?.role === "admin" ||
        this.userData?.role === "supermanager" ||
        this.$Can("show_cib_checklist")
      );
    },
    defaultCompanyId() {
      return !this.userData?.company_id ? null : this.userData?.company_id;
    },
    optionsLookup() {
      return {
        pillar_id: this.createLookupTable(this.pillarOptions),
        pillar_title_id: this.createLookupTable(this.pillarTitleOptions),
        objective_id: this.createLookupTable(this.objectiveOptions),
        action_id: this.createLookupTable(this.actionsOptions),
        owner_id: this.createLookupTable(this.ownerOptions),
        kpi_id: this.createLookupTable(this.kpiOptions),
        wg_id: this.lookupWgOptions(this.workingGroupOptions),
        // companies: this.createLookupTable(this.companiesOptions),
      };
    },
    getReportModalTitle() {
      const titles = {
        start: "Start of Year Action Plan Report",
        mid: "Middle of Year Action Plan Report",
        end: "End of Year Action Plan Report",
        adhoc: "CIS Regional Company Action Plan Report",
        national: "CIS National Progress Report",
      };
      return titles[this.selectedReportType] || "Generate Report";
    },
    isValidReportOptions() {
      if (!this.reportOptions.year) return false;

      let isValid = true;

      if (
        this.selectedReportType === "adhoc" ||
        this.selectedReportType === "national"
      ) {
        isValid = this.reportOptions.columns.length > 0;
      }

      return isValid;
    },
    transformedOwnerOptions() {
      const crossRegionalOwners = this.ownerOptions.filter(
        (owner) => owner.title === "Cross Regional Working Groups"
      );

      const regularOwners = this.ownerOptions
        .filter(
          (owner) => !crossRegionalOwners.map((o) => o.id).includes(owner.id)
        )
        .map((owner) => ({
          id: owner.id,
          label: owner.title,
          isSelectable: true,
        }));

      if (crossRegionalOwners.length >0  && this.workingGroupOptions?.length >0) {
        const crossRegionalNodes = crossRegionalOwners.map((owner) => ({
          id: owner.id,
          label: owner.title,
          children: this.workingGroupOptions.map((wg) => ({
            id: `wg-${wg.id}-${owner.id}`,
            label: wg.group_name,
            isWorkingGroup: true,
            isSelectable: true,
            wg_id: wg.id,
            owner_id: owner.id,
          })),
        }));
        

        return [...regularOwners, ...crossRegionalNodes];
      }

      return regularOwners;
    },
  },

  methods: {
    getToolOptions() {
      return store.dispatch("tools/getToolOptions").then((res) => {
        if (res.data) {
          this.toolOptions = res.data;
          this.pillarOptions = res.data.pillars ?? [];
          this.pillarTitleOptions = res.data.pillarTitles ?? [];
          this.objectiveOptions = res.data.objectives ?? [];
          this.actionsOptions = res.data.actions ?? [];
          this.kpiOptions = res.data.kpis ?? [];
          this.ownerOptions = res.data.owners ?? [];
          this.timelineOptions = res.data.timelines ?? [];
          this.priorities = res.data.priorities ?? [];
          this.workingGroupOptions = res.data.workingGroups ?? [];
          this.companiesOptions = res.data.companies ?? [];

          const crossRegionalOwner = this.ownerOptions.find(
            (o) => o.title === "Cross Regional Working Groups"
          );
          if (crossRegionalOwner) {
            this.crossRegionalOwnerId = crossRegionalOwner.id;
          }
        }
        this.getTools();

        return res;

      });
    },

    getTools() {
      const year = this.priorityYear;
      const previousYear = this.showPreviousYear;
      this.formShow = true;
      return store
        .dispatch("tools/getTools", { year: year, previousYear: previousYear })
        .then((res) => {
          this.tableRows = res.data.map((pillar) => ({
            ...pillar,
            pillar_titles: pillar.pillar_titles.map((title) => ({
              ...title,
              rows: title.rows.map((row) => {
                let owner_selection = [];

                // Handle regular owners from owner_ids
                if (row.owner_ids && Array.isArray(row.owner_ids)) {
                  owner_selection = row.owner_ids
                    .filter((ownerId) => !this.isCrossRegionalOwner(ownerId))
                    .map((ownerId) => ownerId);
                }
              

                // Handle cross regional owners and working groups
               if (
                Array.isArray(row.owner_ids) &&
                Array.isArray(row.wg_ids)
              ) {
                const crossRegionalOwner = row.owner_ids.find((ownerId) =>
                  this.isCrossRegionalOwner(ownerId)
                );

                if (crossRegionalOwner) {
                  const wgSelections = row.wg_ids.map((wgId) => {
                    return `wg-${wgId}-${crossRegionalOwner}`;
                  });
                  owner_selection = [...owner_selection, ...wgSelections];
                }
              }


                return {
                  ...row,
                  owner_selection,
                };
              }),
            })),
          }));
          return res;
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          this.formShow = false;
        });
    },

    handleClickOutside(event) {
      const clickedCell = event.target.closest("td");
      const clickedTable = event.target.closest(".table-responsive");
      const clickedAddRowBtn = event.target.closest(".add-row-btn");
      if (
        !clickedTable ||
        (clickedTable && !clickedCell) ||
        !clickedAddRowBtn
      ) {
        Object.keys(this.editingCell).forEach((rowIndex) => {
          Object.keys(this.editingCell[rowIndex] || {}).forEach((field) => {
            this.$set(this.editingCell[rowIndex], field, false);
          });
        });
      }
    },

    toggleSelect(event, field, rowIndex) {
      event.stopPropagation();
      this.editingCell = {};

      if (this.editingCell[rowIndex]) {
        Object.keys(this.editingCell[rowIndex]).forEach((key) => {
          if (key !== field) {
            this.$set(this.editingCell[rowIndex], key, false);
          }
        });
      }

      if (!this.editingCell[rowIndex]) {
        this.$set(this.editingCell, rowIndex, {});
      }
      this.$set(this.editingCell[rowIndex], field, true);

      this.$nextTick(() => {
        const selectContainer = event.target.closest(".fixed-select");
        const dropdown = selectContainer?.querySelector(".vs__dropdown-menu");

        if (selectContainer && dropdown) {
          const containerRect = selectContainer.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - containerRect.bottom;
          const spaceAbove = containerRect.top;
          const dropdownHeight = 200;

          dropdown.classList.remove("dropdown-up", "dropdown-down");

          if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
            dropdown.classList.add("dropdown-up");
          } else {
            dropdown.classList.add("dropdown-down");
          }

          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (
                mutation.type === "attributes" &&
                mutation.attributeName === "class"
              ) {
                const currentClasses = dropdown.classList;
                if (
                  !currentClasses.contains("dropdown-up") &&
                  !currentClasses.contains("dropdown-down")
                ) {
                  if (
                    spaceBelow < dropdownHeight &&
                    spaceAbove > dropdownHeight
                  ) {
                    dropdown.classList.add("dropdown-up");
                  } else {
                    dropdown.classList.add("dropdown-down");
                  }
                }
              }
            });
          });

          observer.observe(dropdown, { attributes: true });
        }
      });
    },

    hideSelect(event, field, rowIndex) {
      setTimeout(() => {
        const relatedTarget = event.relatedTarget;
        const isClickedOnDropdown =
          relatedTarget &&
          (relatedTarget.classList.contains("vs__dropdown-option") ||
            relatedTarget.closest(".vs__dropdown-menu") ||
            relatedTarget.closest(".vs__dropdown-toggle"));

        if (!isClickedOnDropdown) {
          this.editingCell = {};

          if (rowIndex !== undefined) {
            this.$set(this.editingCell, rowIndex, {
              [field]: false,
            });
          }
        }
      }, 150);
    },

    isEditing(rowIndex, field) {
      if (!this.$Can("update_action_tool_item")) {
        return;
      }
      return (
        this.editingCell[rowIndex] && this.editingCell[rowIndex][field] === true
      );
    },

    addRow() {
      return {
        id: "row_" + Date.now(),
        company_id: this.defaultCompanyId,
        cib_report: false,
        objective_id: null,
        action_id: null,
        national: null,
        timeline: null,
        owner_id: null,
        kpi_id: null,
        wg_ids: null,
        progress: 0,
        onTrack: null,
        additional_comment: null,
        csf_id: null,
        year: Number(this.priorityYear || new Date().getFullYear().toString()),
      };
    },

    addNewPillar() {
      const rowData = this.addRow();
      rowData.pillar_id = null;
      rowData.pillar_title_id = null;

      const newPillar = {
        id: "pillar_" + Date.now(),
        pillar_id: null,
        color: "",
        pillar_titles: [
          {
            pillar_title_id: null,
            rows: [rowData],
          },
        ],
      };

      this.createNewTool(rowData, "addPillar", newPillar);
    },

    addPillarTitle(pillarIndex) {
      const rowData = this.addRow();
      rowData.pillar_id = this.tableRows[pillarIndex].pillar_id;
      rowData.pillar_title_id = null;

      const newPillarTitle = {
        pillar_title_id: null,
        rows: [rowData],
      };

      this.createNewTool(rowData, "addPillarTitle", {
        pillarIndex,
        pillarTitle: newPillarTitle,
      });
    },

    addRowToPillarTitle(pillarIndex, titleIndex) {
      const rowData = this.addRow();
      rowData.pillar_id = this.tableRows[pillarIndex].pillar_id;
      rowData.pillar_title_id = this.tableRows[pillarIndex].pillar_titles[
        titleIndex
      ].pillar_title_id;

      this.createNewTool(rowData, "addRow", {
        pillarIndex,
        titleIndex,
      });
    },

    createNewTool(data, from, context) {
      this.formShow = true;

      store
        .dispatch("tools/createNewTool", data)
        .then((res) => {
          if (res.status === 201) {
            data.id = res.data.tool.id;

            if (from === "addPillar") {
              this.tableRows.push(context);
            } else if (from === "addPillarTitle") {
              this.tableRows[context.pillarIndex].pillar_titles.push(
                context.pillarTitle
              );
            } else if (from === "addRow") {
              this.tableRows[context.pillarIndex].pillar_titles[
                context.titleIndex
              ].rows.push(data);
            }

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Successfully",
                text: "✔️" + res.data.message,
                icon: "ThumbsUpIcon",
                variant: "success",
              },
            });
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "An error occurred",
                text:
                  "An error when create data. Please try again later or contact support.",
                icon: "AlertCircleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((err) => {
          console.log(err);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "An error occurred",
              text:
                "An error occurred while creating tool. Please try again later or contact support.",
              icon: "AlertCircleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.formShow = false;
        });
    },

    getProgressColor(progress) {
      if (progress >= 75) return "#28a745";
      if (progress >= 50) return "#ffc107";
      return "#dc3545";
    },
    downloadPDF() {
      console.log("PDF Downloaded");
    },
    validateProgress(event, row) {
      let value = parseInt(event.target.value);

      if (isNaN(value)) {
        value = 0;
      } else if (value > 100) {
        value = 100;
      } else if (value < 0) {
        value = 0;
      }

      row.progress = value;
      event.target.value = value;

      const updateData = {
        id: row.id,
        progress: value,
        completed: false,
      };

      if (value === 100) {
        updateData.completed = true;
      }

      this.updateTool(updateData);
    },
    deletePillar(pillarIndex) {
      this.$bvModal
        .msgBoxConfirm("Are you sure you want to delete this pillar?", {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes",
          cancelTitle: "No",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then((value) => {
          if (value) {
            // const id = this.tableRows[pillarIndex].id;
            // store.dispatch("tools/deleteTool", id).then((res) => {
            //   if (res.status === 204) {
            //     this.tableRows.splice(pillarIndex, 1);
            //   }
            // });
          }
        });
    },
    deletePillarTitle(pillarIndex, titleIndex) {
      this.$bvModal
        .msgBoxConfirm("Are you sure you want to delete this pillar title?", {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes",
          cancelTitle: "No",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.formShow = true;
            const pillarTitle = this.tableRows[pillarIndex].pillar_titles[
              titleIndex
            ];

            const deletePromises = pillarTitle.rows.map((row) => {
              if (row.id) {
                return store.dispatch("tools/deleteTool", row.id);
              }
              return Promise.resolve();
            });

            Promise.all(deletePromises)
              .then(() => {
                this.tableRows[pillarIndex].pillar_titles.splice(titleIndex, 1);

                if (this.tableRows[pillarIndex].pillar_titles.length === 0) {
                  this.tableRows.splice(pillarIndex, 1);
                }

                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: "Successfully",
                    text:
                      "✔️ Pillar title and related rows deleted successfully",
                    icon: "ThumbsUpIcon",
                    variant: "success",
                  },
                });
              })
              .catch((err) => {
                console.error(err);
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: "Error",
                    text: "An error occurred while deleting the pillar title",
                    icon: "AlertCircleIcon",
                    variant: "danger",
                  },
                });
              })
              .finally(() => {
                this.formShow = false;
              });
          }
        });
    },
    deleteRow(pillarIndex, titleIndex, rowIndex) {
      this.$bvModal
        .msgBoxConfirm("Are you sure you want to delete this row?", {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes",
          cancelTitle: "No",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.formShow = true;
            const row = this.tableRows[pillarIndex].pillar_titles[titleIndex]
              .rows[rowIndex];

            if (!row.id) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "An error occurred",
                  text:
                    "ID not found for delete data. Please try again later or contact support.",
                  icon: "AlertCircleIcon",
                  variant: "danger",
                },
              });
              this.formShow = false;
              return;
            }

            store
              .dispatch("tools/deleteTool", row.id)
              .then((res) => {
                if (res.status === 204) {
                  this.tableRows[pillarIndex].pillar_titles[
                    titleIndex
                  ].rows.splice(rowIndex, 1);

                  if (
                    this.tableRows[pillarIndex].pillar_titles[titleIndex].rows
                      .length === 0
                  ) {
                    this.tableRows[pillarIndex].pillar_titles.splice(
                      titleIndex,
                      1
                    );

                    if (
                      this.tableRows[pillarIndex].pillar_titles.length === 0
                    ) {
                      this.tableRows.splice(pillarIndex, 1);
                    }
                  }

                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "Successfully",
                      text: "✔️ Row deleted successfully",
                      icon: "ThumbsUpIcon",
                      variant: "success",
                    },
                  });
                }
              })
              .catch((err) => {
                console.error(err);
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: "Error",
                    text: "An error occurred while deleting the row",
                    icon: "AlertCircleIcon",
                    variant: "danger",
                  },
                });
              })
              .finally(() => {
                this.formShow = false;
              });
          }
        });
    },
    onPriorityYearChange() {
      this.getTools();
    },
    onlyShowCurrentYear() {
      this.getToolOptions();
    },
    onPreviousYearChange() {
      this.getTools();
    },
    onCibReportChange(row) {
      const totalCibReports = this.tableRows.reduce((count, pillar) => {
        return (
          count +
          pillar.pillar_titles.reduce((titleCount, title) => {
            return (
              titleCount +
              title.rows.filter((r) => r.cib_report && r.id !== row.id).length
            );
          }, 0)
        );
      }, 0);

      // if (row.cib_report && totalCibReports >= 5) {
      //   row.cib_report = false;

      //   this.$toast({
      //     component: ToastificationContent,
      //     props: {
      //       title: "Warning",
      //       text: "Maximum 5 CIB Reports can be selected",
      //       icon: "AlertCircleIcon",
      //       variant: "warning",
      //     },
      //   });

      //   return;
      // }

      const updateData = {
        id: row.id,
        cib_report: row.cib_report,
      };

      this.updateTool(updateData, "cib_report");
    },
    getOptionTitle(id, type) {
      if (!id) return "-";
      return this.optionsLookup[type]?.[id] || "-";
    },
    getOptionOwnerTitle(id, type, isCrossRegionalOwner, wgId) {
      if (!id) return "-";
      if (isCrossRegionalOwner) {
        return (
          this.optionsLookup["wg_id"]?.[wgId] || this.optionsLookup[type]?.[id]
        );
      }
      return this.optionsLookup[type]?.[id] || "-";
    },
    createLookupTable(options) {
      return options.reduce((lookup, option) => {
        lookup[option.id] = option.title;
        return lookup;
      }, {});
    },
    lookupWgOptions(options) {
      return options.reduce((lookup, option) => {
        lookup[option.id] = option.group_name;
        return lookup;
      }, {});
    },
    getFilteredPillarTitles(pillarId) {
      if (!pillarId) return [];
      const selectedPillarTitleIds = this.tableRows
        .flatMap((pillar) =>
          pillar.pillar_id === pillarId
            ? pillar.pillar_titles.map((title) => title.pillar_title_id)
            : []
        )
        .filter((id) => id !== null && id !== this.editingCell.pillarTitleId);

      return this.pillarTitleOptions
        .filter((title) => title.pillar_id === pillarId)
        .map((title) => ({
          ...title,
          disabled: selectedPillarTitleIds.includes(title.id),
        }));
    },
    getFilteredObjectives(pillarTitleId, objectiveId) {
      if (!pillarTitleId) return [];

      const filteredOptions = this.objectiveOptions.filter((obj) => {
        if (obj.pillar_title_id === pillarTitleId) {
          if (this.showCurrentYearOnly) {
            const currentYearPriorities = this.priorities
              .filter((p) => p.year == new Date().getFullYear().toString())
              .map((p) => p.objective_id);

            return currentYearPriorities.includes(obj.id);
          }
          return true;
        }
        return false;
      });

      if (
        objectiveId &&
        !filteredOptions.some((obj) => obj.id === objectiveId)
      ) {
        const selectedObjective = this.objectiveOptions.find(
          (obj) => obj.id === objectiveId
        );
        if (selectedObjective) {
          filteredOptions.unshift(selectedObjective);
        }
      }

      // if (
      //   filteredOptions.length === 0 &&
      //   pillarTitleId &&
      //   !this.priorityYear &&
      //   !this.showCurrentYearOnly
      // ) {
      //   //this.refreshObjectiveOptions(pillarTitleId);
      // }

      return filteredOptions;
    },
    getFilteredActions(objectiveId, currentRowIndices) {
      if (!objectiveId) return [];

      const [
        currentPillarIndex,
        currentTitleIndex,
        currentRowIndex,
      ] = currentRowIndices.split("-").map(Number);

      const selectedActionIds = this.tableRows.flatMap((pillar, pIndex) =>
        pillar.pillar_titles.flatMap((title, tIndex) =>
          title.rows
            .map((row, rIndex) => ({
              actionId: row.action_id,
              objectiveId: row.objective_id,
              isCurrent:
                pIndex === currentPillarIndex &&
                tIndex === currentTitleIndex &&
                rIndex === currentRowIndex,
            }))
            .filter(
              (item) =>
                item.actionId !== null &&
                item.objectiveId === objectiveId &&
                !item.isCurrent
            )
            .map((item) => item.actionId)
        )
      );

      return this.actionsOptions
        .filter((action) => action.objective_id === objectiveId)
        .map((action) => ({
          ...action,
          disabled: selectedActionIds.includes(action.id),
        }));
    },
    getFilteredKPIs(actionId) {
      return this.kpiOptions.filter((kpi) => kpi.action_id === actionId);
    },
    getFilteredPillarOptions() {
      const selectedPillarIds = this.tableRows
        .map((row) => row.pillar_id)
        .filter((id) => id !== null && id !== this.editingCell.pillarId);

      return this.pillarOptions.map((pillar) => ({
        ...pillar,
        disabled: selectedPillarIds.includes(pillar.id),
      }));
    },
    updateTool(data, field = null) {
      // this.formShow = true;
      store
        .dispatch("tools/updateTool", data)
        .then((res) => {
          if (res.status === 200) {
            if (field === "pillar") {
              this.getTools();
            }

            if (this.isCreatedField) {
              this.getToolOptions();
              this.isCreatedField = false;
              this.isOnSelectChangeLoading = false;
            }

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Successfully",
                text: "✔️ Data updated successfully",
                icon: "CheckIcon",
                variant: "success",
              },
            });
          }
        })
        .catch((err) => {
          console.error(err);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "An error occurred while updating the data",
              icon: "AlertCircleIcon",
              variant: "danger",
            },
          });
        });
    },
    refreshObjectiveOptions(pillarTitleId) {
      store
        .dispatch("tools/getToolOptions")
        .then((res) => {
          if (res.data) {
            this.pillarOptions = res.data.pillars ?? [];
            this.pillarTitleOptions = res.data.pillarTitles ?? [];
            this.objectiveOptions = res.data.objectives ?? [];
            this.actionsOptions = res.data.actions ?? [];
            this.kpiOptions = res.data.kpis ?? [];
            this.ownerOptions = res.data.owners ?? [];
            this.timelineOptions = res.data.timelines ?? [];
            this.priorities = res.data.priorities ?? [];
            this.companiesOptions = res.data.companies ?? [];
          }
        })
        .catch((err) => {
          console.error("Error refreshing options:", err);
        });
    },
    onTextareaChange(event, field, rowIndex) {
      const [pillarIndex, titleIndex, detailIndex] = rowIndex
        .split("-")
        .map(Number);
      const row = this.tableRows[pillarIndex].pillar_titles[titleIndex].rows[
        detailIndex
      ];

      const updateData = {
        id: row.id,
        [field === "csf" ? "csf_comment" : "additional_comment"]: event.target
          .value,
      };

      this.updateTool(updateData);
    },
    formatBulletPoints(text) {
      if (!text) return [];
      return text
        .split("•")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    },

    onSelectChange(value, field, rowIndex) {
      if (this.isCreatingNewField) {
        this.isCreatingNewField = false;
        this.isCreatedField = true;
        return;
      }

      let pillarIndex, titleIndex, detailIndex, row;
      let updateData = { id: null };

      if (field === "pillar") {
        pillarIndex = rowIndex;
        const pillarGroup = this.tableRows[pillarIndex];

        this.tableRows[pillarIndex].color =
          this.pillarOptions.find((p) => p.id === value)?.color ?? "white";
        this.tableRows[pillarIndex].pillar_id = value;

        this.tableRows[pillarIndex].pillar_titles = [
          {
            pillar_title_id: null,
            rows: [
              {
                id: pillarGroup.pillar_titles[0]?.rows[0]?.id ?? null,
                company_id: this.defaultCompanyId,
                cib_report: false,
                pillar_id: value,
                pillar_title_id: null,
                objective_id: null,
                action_id: null,
                national: null,
                timeline: null,
                owner_id: null,
                kpi_id: null,
                progress: 0,
                onTrack: null,
                csf_id: null,
                year: Number(this.priorityYear || new Date().getFullYear().toString()),
              },
            ],
          },
        ];

        updateData = {
          id: pillarGroup.pillar_titles[0]?.rows[0]?.id,
          pillar_id: value,
          pillar_title_id: null,
          objective_id: null,
          action_id: null,
          kpi_id: null,
          csf_id: null,
        };
      } else if (field === "pillarTitle") {
        [pillarIndex, titleIndex] = rowIndex.split("-").map(Number);
        const pillarTitle = this.tableRows[pillarIndex].pillar_titles[
          titleIndex
        ];

        pillarTitle.pillar_title_id = value;
        pillarTitle.rows = pillarTitle.rows.map((row) => ({
          ...row,
          pillar_id: this.tableRows[pillarIndex].pillar_id,
          pillar_title_id: value,
          objective_id: null,
          action_id: null,
          kpi_id: null,
          csf_id: null,
        }));

        updateData = {
          id: pillarTitle.rows[0]?.id,
          pillar_id: this.tableRows[pillarIndex].pillar_id,
          pillar_title_id: value,
          objective_id: null,
          action_id: null,
          kpi_id: null,
          csf_id: null,
        };
      } else {
        [pillarIndex, titleIndex, detailIndex] = rowIndex
          .split("-")
          .map(Number);
        row = this.tableRows[pillarIndex].pillar_titles[titleIndex].rows[
          detailIndex
        ];

        const fieldMapping = {
          objective: "objective_id",
          action: "action_id",
          owner: "owner_id",
          kpi: "kpi_id",
          csf: "csf_id",
          national: "national",
          timeline: "timeline",
          onTrack: "onTrack",
          year: "year",
          progress: "progress",
        };

        const backendField = fieldMapping[field] || field;
        row[backendField] = value;
        updateData = { id: row.id, [backendField]: value };

        if (field === "objective") {
          const dependentFields = {
            action_id: null,
            kpi_id: null,
            csf_id: null,
          };
          Object.entries(dependentFields).forEach(([key, val]) => {
            row[key] = val;
            updateData[key] = val;
          });
        } else if (field === "action") {
          const dependentFields = {
            kpi_id: null,
            csf_id: null,
          };
          Object.entries(dependentFields).forEach(([key, val]) => {
            row[key] = val;
            updateData[key] = val;
          });
        } else if (field === "kpi") {
          const dependentFields = {
            csf_id: null,
          };
          Object.entries(dependentFields).forEach(([key, val]) => {
            row[key] = val;
            updateData[key] = val;
          });
        } else if (field === "owner" && !this.isCrossRegionalOwner(value)) {
          const dependentFields = {
            wg_ids: null,
          };
          Object.entries(dependentFields).forEach(([key, val]) => {
            row[key] = val;
            updateData[key] = val;
          });
        }
      }

      if (updateData.id) {
        this.updateTool(updateData, field);
      }

      setTimeout(() => {
        if (!this.editingCell[rowIndex]) {
          this.$set(this.editingCell, rowIndex, {});
        }
        this.$set(this.editingCell[rowIndex], field, false);
      }, 100);
    },
    onTrackChange(value, row) {
      const date = new Date();
      const month = date.getMonth();
      const currentQuarter = row.timeline;

      const isInQuarter = (() => {
        switch (currentQuarter) {
          case "Q1":
            return month >= 0 && month <= 2;
          case "Q2":
            return month >= 3 && month <= 5;
          case "Q3":
            return month >= 6 && month <= 8;
          case "Q4":
            return month >= 9 && month <= 11;
          default:
            return false;
        }
      })();

      if (value === "No" && isInQuarter) {
        this.getCsfComment(row, currentQuarter);
      }
    },

    async getCsfComment(row, quarter) {
      const self = this;
      try {
        const { value: comment } = await this.$swal({
          title: "Critical Success Factors",
          text: "Please enter CSF comment",
          input: "textarea",
          inputPlaceholder: "Enter your CSF comment here...",
          inputAttributes: {
            style: "margin-top:5px!important",
          },
          showCancelButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          confirmButtonColor: "#3085d6",
          confirmButtonText: "Submit",
          customClass: {
            confirmButton: "btn btn-primary",
            text: "mb-1",
          },
          showClass: {
            popup: "animate__animated animate__fadeInDown",
          },
          hideClass: {
            popup: "animate__animated animate__fadeOutUp",
          },
          inputValidator: function(comment) {
            return new Promise(function(resolve) {
              if (comment && comment.trim().length >= 3) {
                resolve();
              } else {
                resolve("Please enter at least 3 characters");
              }
            });
          },
        });

        if (comment) {
          const updateData = {
            id: row.id,
            csf_comment: comment,
            onTrack: "No",
          };
          await this.updateTool(updateData);
          row.csf_comment = comment;
        }
      } catch (error) {
        console.error("Error getting CSF comment:", error);
        row.onTrack = null;
      }
    },

    onCreateNewField(title, type, parentId = null, fieldIndex) {
      this.isCreatingNewField = true;
      if (!this.$Can("create_action_tool_item_field") || !title) {
        this.isCreatingNewField = false;
        return;
      }

      let titleText = null;

      if (typeof title === "string") {
        titleText = title;
      } else {
        titleText = title.title;
      }

      const data = {
        title: titleText,
        type,
      };

      let warnMessageType = null;
      if (type === "objective") {
        data.pillar_title_id = parentId;
        warnMessageType = "Pillar Title";
      } else if (type === "action") {
        data.objective_id = parentId;
        warnMessageType = "Objective";
      } else if (type === "kpi") {
        data.action_id = parentId;
        warnMessageType = "Action";
      }

      if (["objective", "action", "kpi"].includes(type) && !parentId) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Warning",
            text: `Please select ${warnMessageType} for create ${type}`,
            icon: "AlertCircleIcon",
            variant: "warning",
          },
        });
      }

      store
        .dispatch("tools/createNewOption", data)
        .then((res) => {
          if (res.data) {
            const newOption = {
              id: res.data.id,
              title: res.data.title,
            };

            switch (type) {
              case "pillar":
                this.pillarOptions.push(newOption);
                break;
              case "pillarTitle":
                this.pillarTitleOptions.push(newOption);
                break;
              case "objective":
                this.objectiveOptions.push(newOption);
                break;
              case "action":
                this.actionsOptions.push(newOption);
                break;
              case "owner":
                this.ownerOptions.push(newOption);
                break;
              case "kpi":
                this.kpiOptions.push(newOption);
                break;
            }

            this.isOnSelectChangeLoading = true;
            this.onSelectChange(newOption.id, type, fieldIndex);
            this.editingCell = {};

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Successfully",
                text: `✔️ New ${type} created successfully`,
                icon: "CheckIcon",
                variant: "success",
              },
            });

            return newOption.id;
          }
        })
        .catch((err) => {
          console.error(err);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: `An error occurred while creating new ${type}`,
              icon: "AlertCircleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.isCreatingNewField = false;
        });
    },
    isCrossRegionalOwner(ownerId) {
      const owner = this.ownerOptions.find((o) => Number(o.id) === Number(ownerId));
      return owner?.title?.trim().toLowerCase() === "cross regional working groups";
    },

    getWorkingGroupTitle(wgId) {
      if (!wgId) return "";
      const wg = this.workingGroupOptions.find((wg) => wg.id === wgId);
      return wg?.title || "";
    },

    showReportModal(type) {
      this.selectedReportType = type;
      this.showReportModalFlag = true;

      this.reportOptions = {
        year: new Date().getFullYear().toString(),
        sdcb_chairperson: null,
        report_detail: ["Regional", "National"],
        companies: [],
  pillarId: null,
        columns: [
          { label: "Name Of Objective", value: "objective" },
          { label: "Actions", value: "actions" },
          { label: "KPI", value: "kpi" },
          { label: "Timeline", value: "timeline" },
        ],
      };
    },
    generateReport() {
      const payload = {
        type: this.selectedReportType,
        ...this.reportOptions,
      };

      this.formShow = true;
      store
        .dispatch("tools/downloadPDF3", payload)
        .then((response) => {
          if (response.status === 200) {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;

            let fileName = "action_report.pdf";

            if (this.selectedReportType == "start") {
              const now = new Date();
              fileName = `startofyear-report_${now.getFullYear()}-${String(
                now.getMonth() + 1
              ).padStart(2, "0")}-${String(now.getDate()).padStart(
                2,
                "0"
              )}.pdf`;
            } else if (this.selectedReportType == "mid") {
              const now = new Date();
              fileName = `midyear-report_${now.getFullYear()}-${String(
                now.getMonth() + 1
              ).padStart(2, "0")}-${String(now.getDate()).padStart(
                2,
                "0"
              )}.pdf`;
            } else if (this.selectedReportType == "end") {
              const now = new Date();
              fileName = `endofyear-report_${now.getFullYear()}-${String(
                now.getMonth() + 1
              ).padStart(2, "0")}-${String(now.getDate()).padStart(
                2,
                "0"
              )}.pdf`;
            } else if (this.selectedReportType == "adhoc") {
              const now = new Date();
              fileName = `adhoc-report_${now.getFullYear()}-${String(
                now.getMonth() + 1
              ).padStart(2, "0")}-${String(now.getDate()).padStart(
                2,
                "0"
              )}_${String(now.getHours()).padStart(2, "0")}-${String(
                now.getMinutes()
              ).padStart(2, "0")}.pdf`;
            } else if (this.selectedReportType == "national") {
              const now = new Date();
              fileName = `national-report_${now.getFullYear()}-${String(
                now.getMonth() + 1
              ).padStart(2, "0")}-${String(now.getDate()).padStart(
                2,
                "0"
              )}.pdf`;
            }

            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                text: "Report generated successfully",
                icon: "CheckIcon",
                variant: "success",
              },
            });

            this.showReportModalFlag = false;
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Not found Report data",
                text: "Report data not found for these options.",
                icon: "AlertCircleIcon",
                variant: "warning",
              },
            });
          }
        })
        .catch((error) => {
          console.log(error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "Failed to generate report",
              icon: "AlertCircleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.formShow = false;
        });
    },
    handlePopoverShow() {
      this.isFirstShow = false;
    },
    getOwnerDisplayText(row) {
      if (!row.owner_ids && !row.owner_id) return "Select Owner";

      const ownerIds =
        row.owner_ids ||
        (Array.isArray(row.owner_id) ? row.owner_id : [row.owner_id]);
      const displayTexts = [];

      ownerIds.forEach((ownerId) => {
        if (this.isCrossRegionalOwner(ownerId) && row.wg_ids) {
          // Display working group names for cross regional owners
          row.wg_ids.forEach((wgId) => {
            const wgName = this.optionsLookup["wg_id"]?.[wgId];
            if (wgName) displayTexts.push(wgName);
          });
        } else {
          // Display regular owner names
          const ownerName = this.optionsLookup["owner_id"]?.[ownerId];
          if (ownerName) displayTexts.push(ownerName);
        }
      });

      return displayTexts.length > 0 ? displayTexts.join(", ") : "Select Owner";
    },
    handleOwnerSelection(selectedValues, row, rowIndex) {
      // Extract owner IDs and working group IDs
      const ownerIds = [];
      const wgIds = [];

      selectedValues.forEach((value) => {
        if (typeof value === "string" && value.startsWith("wg-")) {
          // Handle working group selection
          const [_, wgId, ownerId] = value.split("-");
          if (!ownerIds.includes(Number(ownerId))) {
            ownerIds.push(Number(ownerId));
          }
          wgIds.push(Number(wgId));
        } else {
          // Handle regular owner selection
          ownerIds.push(Number(value));
        }
      });

      // Update the row data
      row.owner_ids = ownerIds;
      row.owner_id = ownerIds; // Keep for backwards compatibility
      row.wg_ids = wgIds.length > 0 ? wgIds : null;

      // Prepare update data
      const updateData = {
        id: row.id,
        owner_ids: ownerIds,
        wg_ids: wgIds.length > 0 ? wgIds : null,
      };

      // Call API to update
      this.updateTool(updateData);
    },
    ownerNormalizer(node) {
      return {
        id: node.id,
        label: node.label,
        isWorkingGroup: node.isWorkingGroup,
        children: node.children,
      };
    },
    onDragEnd(event, pillarIndex, titleIndex) {
      const rows = this.tableRows[pillarIndex].pillar_titles[titleIndex].rows;
      const pillarId = this.tableRows[pillarIndex].pillar_id;
      const pillarTitleId = this.tableRows[pillarIndex].pillar_titles[
        titleIndex
      ].pillar_title_id;

      const updateData = {
        pillar_id: pillarId,
        pillar_title_id: pillarTitleId,
        rows: rows.map((row, index) => ({
          id: row.id,
          row_order: index + 1,
        })),
      };

      store
        .dispatch("tools/updateRowOrder", updateData)
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Successfully",
              text: "✔️ Row order updated successfully",
              icon: "CheckIcon",
              variant: "success",
            },
          });
        })
        .catch((err) => {
          console.error(err);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "An error occurred while updating row order",
              icon: "AlertCircleIcon",
              variant: "danger",
            },
          });
        });
    },
  },
};
</script>

<style scoped>
/* Table container için stil */
.table-responsive {
  overflow-x: auto;
  max-width: 100%;
  position: relative;
  z-index: 1;
}

/* v-select container düzenlemesi */
.fixed-select {
  position: relative !important;
}

/* v-select dropdown için pozisyon ayarı */
.vs__dropdown-menu {
  position: fixed !important;
  z-index: 99999 !important;
  width: auto !important;
  min-width: 160px !important;
  max-width: 300px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}

/* Pillar select için özel ayar */
.pillar-select >>> .vs__dropdown-menu {
  min-width: 200px !important;
}

.pillar-title-select >>> .vs__dropdown-menu {
  min-width: 200px !important;
}

.fixed-timeline-select {
  width: 70px !important;
  max-width: 70px !important;
  min-width: 70px !important;
}

/* Timeline select için özel ayar */
.fixed-timeline-select >>> .vs__dropdown-menu {
  min-width: 70px !important;
  width: 70px !important;
}

/* Progress ve On Track select için özel ayar */
.fixed-progress-select >>> .vs__dropdown-menu,
.fixed-ontrack-select >>> .vs__dropdown-menu {
  min-width: 85px !important;
  width: 85px !important;
}

/* Table hücreleri için overflow kontrolü */
.table td:not(:last-child) {
  position: relative !important;
  overflow: visible !important;
}

/* v-select toggle button için stil */
.vs__dropdown-toggle {
  position: relative !important;
  z-index: 1 !important;
}

/* Tablo Responsive */
.table-responsive {
  overflow-x: auto;
  max-width: 100%;
  z-index: 1;
  height: 75vh;
  position: relative;
}

/* Tablo Başlıkları */
.table thead th {
  font-size: 10px;
  font-weight: bold;
  text-align: center;
  white-space: nowrap;
  padding: 0.3rem;
}

/* Tablo hücrelerinin düzenlenmesi */
.table tbody td:not(:last-child) {
  max-width: 160px;
  min-width: 160px;
  vertical-align: middle;
  white-space: nowrap;
  overflow: visible !important;
  padding: 0.3rem;
  position: relative;
}

/* v-select içeren hücrelerin düzenlenmesi */
.fixed-select {
  position: static !important;
  width: 150px !important;
  min-width: 150px !important;
  max-width: 150px !important;
  margin: 0 auto;
}

/* v-select container düzenlemesi */
.fixed-select >>> .vs__dropdown-toggle {
  padding: 2px 24px 2px 4px !important;
  min-height: 32px !important;
  max-height: 32px !important;
  background: #f8fafc !important;
  border: 1px solid #cbd5e0 !important;
  display: flex;
  align-items: center;
}

/* Selected options container düzenlemesi */
.fixed-select >>> .vs__selected-options {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  width: calc(100% - 28px) !important;
  height: 30px !important;
}

/* Seçili metin için düzenleme */
.fixed-select >>> .vs__selected {
  font-weight: 500 !important;
  margin: 0 !important;
  padding: 0 4px !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  font-size: 0.8rem !important;
  color: #2c5282 !important;
  letter-spacing: 0.2px !important;
  width: 100% !important;
  min-width: 0 !important;
  height: 30px !important;
  line-height: 30px !important;
  display: block !important;
}

/* Actions butonları için düzenleme */
.fixed-select >>> .vs__actions {
  padding: 0 !important;
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 2px;
  margin: 0 !important;
  height: 30px !important;
  min-width: 24px !important;
}

.fixed-select >>> .vs--searching .vs__selected-options {
  opacity: 0 !important;
}

.fixed-select >>> .vs--searching .vs__search {
  position: absolute !important;
  width: calc(100% - 28px) !important;
  padding: 0 4px !important;
  border: none !important;
  height: 30px !important;
  margin: 0 !important;
  line-height: 30px !important;
  font-size: 0.8rem !important;
  color: #008bcc !important;
  background: transparent !important;
  opacity: 1 !important;
  z-index: 2 !important;
}

/* Search input aktif değilken */
.fixed-select >>> .vs__search:not(:focus) {
  position: absolute !important;
  width: 0 !important;
  padding: 0 !important;
  opacity: 0 !important;
}

/* Dropdown açıkken search input düzenlemesi */
.fixed-select >>> .vs--open .vs__selected {
  position: absolute !important;
  opacity: 0 !important;
}

/* Search aktif olduğunda selected-options container'ı gizle */
.fixed-select >>> .vs--searching .vs__selected-options {
  visibility: hidden !important;
}

/* Add these new styles */
.date-container {
  background: #f8f9fa;
  padding: 12px 15px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.date-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
}

.date-value {
  color: #2c5282;
  font-weight: 500;
  font-size: 0.95rem;
}

/* Switch için yeni stiller */
.custom-switch {
  margin-bottom: 0;
}

.custom-switch >>> .custom-control-label {
  padding-left: 0.5rem !important;
}

.switch-label {
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 600;
}

.pillar-cell {
  padding: 8px !important;
  background-color: #f8f9fa !important;
  border-bottom: 2px solid #dee2e6 !important;
}

.pillar-select {
  width: 100% !important;
  max-width: 100% !important;
}
.pillar-title-select {
  width: 100% !important;
  max-width: 100% !important;
}

.pillar-select >>> .vs__dropdown-toggle {
  width: 100% !important;
  max-width: 100% !important;
}

.pillar-title-select >>> .vs__dropdown-toggle {
  width: 100% !important;
  max-width: 100% !important;
}

/* CSF kolonu için özel genişlik ayarı */
.table tbody td:last-child {
  width: 100% !important;
  padding: 4.2px !important;
}

.table tbody td:last-child .fixed-select {
  width: 100% !important;
  max-width: 100% !important;
}

.table tbody td:last-child .fixed-select >>> .vs__dropdown-toggle {
  width: 100% !important;
  max-width: 100% !important;
}

/* Normal durumdaki hücre stili */
.cell-text {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: normal !important;
  line-height: 1.4 !important;
  min-height: 40px !important;
  padding: 4px 0 !important;
  word-break: break-word !important;
  font-size: 0.8rem !important;
  color: #0d0d0f !important;
  letter-spacing: 0.2px !important;
  font-weight: 500 !important;
  display: flex !important;
  align-items: center !important;
}

.pillar-text {
  font-weight: bold !important;
  color: #2d3748 !important;
  font-size: 1rem !important;
  padding-left: 10px !important;
}

.pillar-title-text {
  font-weight: 600 !important;
  color: #2d3748 !important;
  font-size: 0.85rem !important;
  padding-left: 10px !important;
  min-height: 20px !important;
  line-height: 20px !important;
  height: 20px !important;
}

/* Placeholder style for cell-text */
.cell-text.placeholder {
  color: #6e6b7b !important;
  font-weight: normal !important;
}

/* Edit modundaki hücre stili */
.editing .fixed-select,
.editing .progress-input {
  display: block !important;
  opacity: 1 !important;
  visibility: visible !important;
  position: relative !important;
  z-index: 1000 !important;
}

/* Normal durumdaki input ve select gizleme */
.fixed-select,
.progress-input {
  display: none !important;
  opacity: 0 !important;
  visibility: hidden !important;
}

/* Özel sütun stilleri */
.owner-column,
.kpi-column,
.progress-column,
.ontrack-column {
  position: relative !important;
}

.fixed-year-select {
  width: 70px !important;
  max-width: 70px !important;
  min-width: 70px !important;
}

/* v-select dropdown için z-index ayarı */
.vs__dropdown-menu {
  z-index: 99999 !important;
}

/* Tüm hücreler için temel stiller (last-child hariç) */
.table td:not(:last-child) {
  position: relative !important;
  overflow: visible !important;
}

/* Tbody hücreleri için detaylı stiller (last-child hariç) */
.table tbody td:not(:last-child) {
  max-width: 160px !important;
  min-width: 160px !important;
  vertical-align: middle !important;
  white-space: nowrap !important;
  overflow: visible !important;
  padding: 0.3rem !important;
  position: relative !important;
}

/* Action column sticky positioning with class */
.sticky-action-column {
  position: sticky !important;
  right: 0 !important;
  z-index: 99999 !important;
  background-color: white !important;
  width: 60px !important;
  min-width: 60px !important;
  max-width: 60px !important;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1) !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

/* Add row cell sticky positioning */
.sticky-add-row {
  position: sticky !important;
  right: 0 !important;
  z-index: 2 !important;
  background-color: white !important;
  width: 60px !important;
  min-width: 60px !important;
  max-width: 60px !important;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1) !important;
}

.sticky-pillar-title-row {
  position: sticky !important;
  right: 0 !important;
  z-index: 2 !important;
  background-color: transparent !important;
  width: 60px !important;
  min-width: 60px !important;
  max-width: 60px !important;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1) !important;
}

/* Table header için z-index ayarı */
.table thead th {
  position: sticky !important;
  top: 0 !important;
  background: #008bcc !important;
  color: white !important;
  z-index: 4 !important;
  border-bottom: 2px solid #dee2e6 !important;
  font-size: 10px !important;
  font-weight: bold !important;
  text-align: center !important;
  white-space: nowrap !important;
  padding: 0.3rem !important;
}

/* Son sütun header'ı için özel stil */
.table thead th:last-child {
  z-index: 5 !important;
  background-color: #2c5282 !important;
  color: white !important;
}

/* Add row ve Delete row butonları için sticky container */
.add-row-btn,
.delete-row-btn {
  position: sticky !important;
  right: 8px !important;
  padding: 0.2rem 0.5rem !important;
  font-size: 0.8rem !important;
}

.pillar-title-action-btn {
  position: sticky !important;
  right: 8px !important;
  padding: 0.2rem 0.5rem !important;
  font-size: 0.8rem !important;
  max-height: 20px !important;
  height: 20px !important;
  min-height: 20px !important;
  white-space: nowrap !important;
}

.pillar-title-action-btn:last-child {
  margin-left: 5px !important;
}

/* Add row butonu için özel stil */

.add-row-btn {
  margin: 0 auto !important;
  display: block !important;
}

/* Delete row butonu için özel stil */
.delete-row-btn {
  margin: 0 auto !important;
  display: block !important;
}

/* Buton container'ları için ortalama */
.sticky-action-column,
.sticky-add-row {
  text-align: center !important;
}

/* Table cell width and text styling */
::v-deep .pillar-title-cell,
::v-deep .objective-cell,
::v-deep .actions-cell {
  width: 400px !important;
  min-width: 400px !important;
  max-width: 400px !important;
  padding: 8px !important;
}

/* Cell content text styling with 2-line ellipsis */
::v-deep .pillar-title-cell .cell-content,
::v-deep .objective-cell .cell-content,
::v-deep .actions-cell .cell-content {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: normal !important;
  line-height: 1.4 !important;
  max-height: 2.8em !important;
  word-break: break-word !important;
  min-height: 40px !important;
}

/* Table header styling */
::v-deep .pillar-title-header,
::v-deep .objective-header,
::v-deep .actions-header {
  width: 400px !important;
  min-width: 400px !important;
  max-width: 400px !important;
  white-space: nowrap !important;
}

/* Table cell height adjustment */
::v-deep tbody td {
  vertical-align: top !important;
  height: auto !important;
  min-height: 50px !important;
}

/* Ensure table doesn't break layout */
.table-responsive {
  overflow-x: auto !important;
}

.table {
  width: max-content !important;
  min-width: 100% !important;
}

/* Optional: Add hover effect for better readability */
::v-deep .pillar-title-cell .cell-content:hover,
::v-deep .objective-cell .cell-content:hover,
::v-deep .actions-cell .cell-content:hover {
  overflow: visible !important;
  display: block !important;
  position: relative !important;
  z-index: 1 !important;
  background-color: #fff !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  padding: 4px !important;
  border-radius: 4px !important;
  max-height: none !important;
  -webkit-line-clamp: unset !important;
}

/* Vue-select width adjustments for specific columns */
::v-deep .pillar-title-select,
::v-deep .objective-select,
::v-deep .actions-select {
  width: 400px !important;
  min-width: 400px !important;
  max-width: 400px !important;
}

::v-deep .pillar-title-select .vs__dropdown-toggle,
::v-deep .objective-select .vs__dropdown-toggle,
::v-deep .actions-select .vs__dropdown-toggle {
  width: 380px !important;
  min-width: 380px !important;
  max-width: 380px !important;
}

::v-deep .pillar-title-select .vs__dropdown-menu,
::v-deep .objective-select .vs__dropdown-menu,
::v-deep .actions-select .vs__dropdown-menu {
  width: 400px !important;
  min-width: 400px !important;
  max-width: 400px !important;
}

/* Selected text and options styling */
::v-deep .pillar-title-select .vs__selected,
::v-deep .objective-select .vs__selected,
::v-deep .actions-select .vs__selected {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: normal !important;
  line-height: 1.4 !important;
  max-height: 2.8em !important;
  word-break: break-word !important;
}

/* Dropdown option styling */
::v-deep .pillar-title-select .vs__dropdown-option,
::v-deep .objective-select .vs__dropdown-option,
::v-deep .actions-select .vs__dropdown-option {
  white-space: normal !important;
  word-wrap: break-word !important;
  padding: 8px !important;
  line-height: 1.4 !important;
}

/* Remove tick icons */
::v-deep .pillar-title-select .vs__actions .vs__open-indicator,
::v-deep .objective-select .vs__actions .vs__open-indicator,
::v-deep .actions-select .vs__actions .vs__open-indicator {
  display: none !important;
}

.fixed-select.pillar-title-select >>> .vs__actions,
.fixed-select.objective-select >>> .vs__actions,
.fixed-select.actions-select >>> .vs__actions {
  right: 12px !important;
}

/* Style for span text content (when not editing) */
::v-deep .pillar-title-cell span,
::v-deep .objective-cell span,
::v-deep .actions-cell span {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: normal !important;
  line-height: 1.4 !important;
  max-height: 2.8em !important;
  word-break: break-word !important;
  min-height: 40px !important;
}

/* Adjust dropdown toggle padding since we removed the tick */
::v-deep .pillar-title-select .vs__dropdown-toggle,
::v-deep .objective-select .vs__dropdown-toggle,
::v-deep .actions-select .vs__dropdown-toggle {
  padding-right: 8px !important;
}

/* Ensure consistent text alignment in both states */
::v-deep .pillar-title-cell,
::v-deep .objective-cell,
::v-deep .actions-cell {
  vertical-align: top !important;
}

.pillar-cell {
  padding: 8px !important;
  background-color: #f8f9fa !important;
  border-bottom: 2px solid #dee2e6 !important;
}

/* Pillar title hücresi için özel stil */
.pillar-cell:has(.pillar-title-text) {
  padding: 2px 12px !important;
  min-height: 24px !important;
  height: 24px !important;
}

.pillar-title-text {
  font-weight: 600 !important;
  color: #2d3748 !important;
  font-size: 0.85rem !important;
  padding-left: 10px !important;
  min-height: 20px !important;
  line-height: 20px !important;
  height: 20px !important;
}

/* v-select için pillar title özel stilleri */
.pillar-title-select >>> .vs__dropdown-toggle {
  min-height: 24px !important;
  max-height: 24px !important;
  height: 24px !important;
}

.pillar-title-select >>> .vs__selected-options {
  min-height: 22px !important;
  max-height: 22px !important;
  height: 22px !important;
}

.pillar-title-select >>> .vs__selected {
  margin: 0 !important;
  padding: 0 4px !important;
  line-height: 22px !important;
  height: 22px !important;
}

/* Pillar title satırı için özel stil */
tr:has(.pillar-title-text) {
  height: 24px !important;
}

tr:has(.pillar-title-text) td {
  height: 24px !important;
  padding: 2px 16px !important;
}

/* Add new styles for CSF textarea */
.csf-comment-input {
  width: 100%;
  min-height: 40px;
  font-size: 0.8rem;
  padding: 4px;
  resize: vertical;
}

/* Update existing styles */
.csf-cell,
.additional-comment-cell {
  width: 400px !important;
  min-width: 400px !important;
  max-width: 400px !important;
  padding: 8px !important;
}

.csf-comment-input,
.additional-comment-input {
  width: 380px !important;
  min-width: 380px !important;
  max-width: 380px !important;
  min-height: 40px;
  font-size: 0.8rem;
  padding: 4px;
  resize: vertical;
}

/* Update cell-text for these columns */
.csf-cell .cell-text,
.actions-span-cell .cell-text,
.objective-span-cell .cell-text,
.additional-comment-cell .cell-text {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: normal !important;
  line-height: 1.2 !important;
  min-height: 32px !important;
  max-height: 32px !important;
  height: 32px !important;
  width: 230px !important;
  padding: 4px 8px !important;
}

.actions-span-cell .cell-text {
  width: 400px !important;
}

.objective-span-cell .cell-text {
  width: 400px !important;
}

/* Update header and cell widths */
.csf-header,
.additional-comment-header,
.csf-cell,
.additional-comment-cell {
  width: 250px !important;
  min-width: 250px !important;
  max-width: 250px !important;
  padding: 8px !important;
}

.csf-comment-input,
.additional-comment-input {
  width: 230px !important;
  min-width: 230px !important;
  max-width: 230px !important;
  min-height: 40px;
  font-size: 0.8rem;
  padding: 4px;
  resize: vertical;
}

/* Update cell-text width */
.csf-cell .cell-text,
.additional-comment-cell .cell-text {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: normal !important;
  line-height: 1.2 !important;
  max-height: 2.8em !important;
  word-break: break-word !important;
  min-height: 40px !important;
  width: 230px !important;
}

/* Update header styles */
.table thead th {
  position: sticky !important;
  top: 0 !important;
  background: #008bcc !important;
  color: white !important;
  z-index: 4 !important;
  border-bottom: 2px solid #dee2e6 !important;
  font-size: 10px !important;
  font-weight: bold !important;
  text-align: center !important;
  white-space: nowrap !important;
  padding: 0.3rem !important;
  height: 20px !important; /* Add fixed height for headers */
}

/* Update objective and actions column widths to match */
::v-deep .pillar-title-cell,
::v-deep .objective-cell,
::v-deep .actions-cell {
  width: 400px !important;
  min-width: 400px !important;
  max-width: 400px !important;
  padding: 8px !important;
}

::v-deep .pillar-title-select,
::v-deep .objective-select,
::v-deep .actions-select {
  width: 400px !important;
  min-width: 400px !important;
  max-width: 400px !important;
}

::v-deep .pillar-title-select .vs__dropdown-toggle,
::v-deep .objective-select .vs__dropdown-toggle,
::v-deep .actions-select .vs__dropdown-toggle {
  width: 380px !important;
  min-width: 380px !important;
  max-width: 380px !important;
}

::v-deep .pillar-title-select .vs__dropdown-menu,
::v-deep .objective-select .vs__dropdown-menu,
::v-deep .actions-select .vs__dropdown-menu {
  width: 380px !important;
  min-width: 380px !important;
  max-width: 380px !important;
}

/* Update header specific styles */
.csf-header,
.additional-comment-header {
  background: #008bcc !important;
  color: white !important;
  font-size: 10px !important;
  font-weight: bold !important;
  text-align: center !important;
  white-space: nowrap !important;
  padding: 0.3rem !important;
  height: 40px !important;
}

/* Update textarea styles */
.csf-comment-input,
.additional-comment-input {
  width: 230px !important;
  min-width: 230px !important;
  max-width: 230px !important;
  min-height: 32px !important;
  max-height: 32px !important;
  height: 32px !important;
  font-size: 0.8rem !important;
  padding: 4px 8px !important;
  resize: none !important; /* Remove resize handle */
  overflow-y: auto !important; /* Add scrollbar if needed */
  line-height: 1.2 !important;
}

/* Update cell height to match */
.csf-cell .cell-text,
.additional-comment-cell .cell-text {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: normal !important;
  line-height: 1.2 !important;
  min-height: 32px !important;
  max-height: 32px !important;
  height: 32px !important;
  width: 230px !important;
  padding: 4px 8px !important;
}

/* Add these new styles */
.popover-content-list {
  max-width: 400px;
  white-space: normal;
}

.popover-list-item {
  padding: 4px 0;
  line-height: 1.4;
  text-align: left;
}

/* Override Bootstrap popover max-width if needed */
::v-deep .popover {
  max-width: 400px !important;
}

/* Update existing styles */
.kpi-column {
  width: 250px !important;
  min-width: 250px !important;
  max-width: 250px !important;
}

.kpi-column .cell-text {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: normal !important;
  line-height: 1.2 !important;
  min-height: 32px !important;
  max-height: 32px !important;
  padding: 4px 8px !important;
}

/* Ensure consistent popover styling across all columns */
::v-deep .popover {
  max-width: 400px !important;
}

.popover-content-list {
  max-width: 400px;
  white-space: normal;
}

.popover-list-item {
  padding: 4px 0;
  line-height: 1.4;
  text-align: left;
}

/* Consistent v-select styling */
.kpi-column .fixed-select {
  width: 230px !important;
  min-width: 230px !important;
  max-width: 230px !important;
}

/* Search input aktif olduğunda seçili değeri gizle */
.fixed-select.vs--searching >>> .vs__selected {
  display: none !important;
  visibility: hidden !important;
}

/* Search input aktif olmadığında seçili değeri göster */
.fixed-select >>> .vs__selected {
  display: block !important;
  visibility: visible !important;
}

/* CIB Report column styles */
.cib-report-cell {
  vertical-align: middle !important;
  padding: 4px !important;
  text-align: center !important;
}

/* Checkbox alignment */
.custom-control-primary {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  height: 100% !important;
  width: 100% !important;
  margin: 0 auto !important;
}

/* Target the actual checkbox input */
.custom-control-primary >>> .custom-control-input {
  position: relative !important;
  margin: 0 auto !important;
  left: 0 !important;
}

/* Target the custom checkbox label */
.custom-control-primary >>> .custom-control-label {
  padding-left: 0 !important;
  margin: 0 auto !important;
}

.custom-control-primary >>> .custom-control-label::before,
.custom-control-primary >>> .custom-control-label::after {
  left: 50% !important;
  transform: translateX(-50%) !important;
}

.table thead th.cib-report-header {
  width: 50px !important;
  min-width: 50px !important;
  max-width: 50px !important;
  text-align: center !important;
}

/* Move the background color from cell to row level */
tr.cib-report-selected {
  background-color: rgba(82, 199, 207, 0.25) !important;
}

/* Add these styles to your existing <style> section */
.cib-checkbox-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.cib-text {
  position: absolute;
  font-weight: bold;
  color: #008bcc;
  font-size: 0.8rem;
}

.hover-visible {
  opacity: 0;
}

.cib-checkbox-container:hover .cib-text {
  display: none;
}

.cib-checkbox-container:hover .hover-visible {
  opacity: 1;
}

.wg-badge {
  position: absolute !important;
  top: 2px !important;
  right: 2px !important;
  font-size: 0.7rem !important;
  padding: 0.2rem 0.5rem !important;
  z-index: 2 !important;
}

.top-btns {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.action-row-btn {
  width: 100% !important;
  margin-bottom: 0.5rem !important;
  text-align: left !important;
  white-space: nowrap !important;
}

.action-row-btn:last-child {
  margin-bottom: 0 !important;
}

.trades-font {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  width: 100% !important;
}

.wg-badge {
  max-width: 120px;
}

.wg-badge-text {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wg-badge-text:hover {
  cursor: default;
}

/* Add these new styles */
.wg-select {
  position: static !important;
  width: 200px !important;
  min-width: 200px !important;
  max-width: 200px !important;
  margin: 0 auto;
}

.wg-select >>> .vs__dropdown-toggle {
  padding: 2px 24px 2px 4px !important;
  min-height: 32px !important;
  max-height: 32px !important;
  background: #f8fafc !important;
  border: 1px solid #cbd5e0 !important;
  display: flex;
  align-items: center;
  position: relative !important; /* Add this */
  overflow: hidden !important; /* Add this */
}

.wg-select >>> .vs__selected-options {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  width: calc(100% - 28px) !important;
  height: 30px !important;
  position: relative !important; /* Add this */
  z-index: 1 !important; /* Add this */
}

.wg-select >>> .vs__selected {
  font-weight: 500 !important;
  margin: 0 !important;
  padding: 0 4px !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  font-size: 0.8rem !important;
  color: #2c5282 !important;
  letter-spacing: 0.2px !important;
  width: 100% !important;
  min-width: 0 !important;
  height: 30px !important;
  line-height: 30px !important;
  display: block !important;
}

.wg-select >>> .vs__actions {
  padding: 0 !important;
  position: absolute !important;
  right: 4px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  display: flex !important;
  align-items: center !important;
  gap: 2px !important;
  margin: 0 !important;
  height: 30px !important;
  min-width: 24px !important;
  background: #f8fafc !important; /* Add this */
  z-index: 2 !important; /* Add this */
}

.wg-select >>> .vs--searching .vs__selected-options {
  opacity: 0 !important;
}

.wg-select >>> .vs--searching .vs__search {
  position: absolute !important;
  width: 100% !important;
  padding: 0 4px !important;
  border: none !important;
  height: 30px !important;
  margin: 0 !important;
  line-height: 30px !important;
  font-size: 0.8rem !important;
  color: #008bcc !important;
  background: transparent !important;
  opacity: 1 !important;
  z-index: 2 !important;
}

.wg-select >>> .vs__search:not(:focus) {
  position: absolute !important;
  width: 0 !important;
  padding: 0 !important;
  opacity: 0 !important;
}

.wg-select >>> .vs--open .vs__selected {
  position: absolute !important;
  opacity: 0 !important;
}

.wg-select >>> .vs--searching .vs__selected-options {
  visibility: hidden !important;
}

.wg-select >>> .vs__dropdown-option {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

/* Add these new styles for icons */
.wg-select >>> .vs__clear {
  margin-right: 4px !important;
  transform: scale(0.8) !important;
}

.wg-select >>> .vs__open-indicator {
  transform: scale(0.8) !important;
}

.wg-select >>> .vs--open .vs__open-indicator {
  transform: rotate(180deg) scale(0.8) !important;
}

.wg-select >>> .vs__deselect {
  margin-right: 4px !important;
  transform: scale(0.8) !important;
}

/* Treeselect container genişliğini ayarlama */
.vue-treeselect {
  max-width: 300px; /* veya istediğiniz genişlik */
}

/* Seçili değerlerin container'ını özelleştirme */
.vue-treeselect__value-container {
  max-height: 60px;
  overflow-y: auto;
}

/* Report menu container and button styles */
.report-menu-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
}

.report-menu-container .btn {
  width: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: 0.85rem;
}

.report-menu-container .btn .trades-font {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.report-modal-content {
  padding: 1rem;
}

/* Owner column specific width */
.owner-column {
  width: 220px !important;
  min-width: 220px !important;
  max-width: 220px !important;
}

/* Owner select specific width */
.owner-column .fixed-select {
  width: 210px !important;
  min-width: 210px !important;
  max-width: 210px !important;
}

.drag-handle-cell {
  width: 30px !important;
  min-width: 30px !important;
  max-width: 30px !important;
  text-align: center;
  vertical-align: middle;
  padding: 0 !important;
}

.drag-handle {
  color: #6e6b7b;
  cursor: move;
}

.drag-handle:hover {
  color: #5e5873;
}

.owner-column {
  max-height: 40px !important;
  overflow: hidden !important;
  position: relative !important;
}

.owner-column .cell-text {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: normal !important;
  line-height: 1.2 !important;
  min-height: 32px !important;
  max-height: 32px !important;
  padding: 4px 8px !important;
}

.owner-text {
  white-space: normal !important;
}

.truncate-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<style>
.working-group-popover {
  min-width: 300px !important;
  width: 100% !important;
  max-width: 600px !important;
}

.wide-select.wg-select {
  width: 100% !important;
  min-width: 300px !important;
  max-width: 600px !important;
}

.wide-select >>> .vs__dropdown-menu {
  min-width: 300px !important;
  max-width: 600px !important;
}

.vs__dropdown-menu {
  z-index: 99999 !important;
}
.animate__animated {
  animation-fill-mode: none;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "~vue-context/src/sass/vue-context";

// .v-select .vs__dropdown-menu {
//   top: auto !important;
//   bottom: calc(100% + 4px);
// }

/* ... existing styles ... */

/* Add these new styles */
.dropdown-up.vs__dropdown-menu {
  bottom: 100% !important;
  top: auto !important;
  border-bottom: none !important;
  border-top: 1px solid rgba(60, 60, 60, 0.26) !important;
  margin-bottom: 4px !important;
  margin-top: 0 !important;
}

.dropdown-down.vs__dropdown-menu {
  top: 100% !important;
  bottom: auto !important;
  border-top: none !important;
  border-bottom: 1px solid rgba(60, 60, 60, 0.26) !important;
  margin-top: 4px !important;
  margin-bottom: 0 !important;
}
</style>

<style scoped>
/* Small v-select styling */
.small-select >>> .vs__dropdown-toggle {
  padding: 2px 4px !important;
  min-height: 28px !important;
  max-height: 28px !important;
  background: #f8fafc !important;
  border: 1px solid #cbd5e0 !important;
  display: flex;
  align-items: center;
}

.small-select >>> .vs__selected {
  font-size: 0.8rem !important;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 24px !important;
}

.small-select >>> .vs__actions {
  padding: 2px 4px !important;
}

.small-select >>> .vs__search::placeholder,
.small-select >>> .vs__dropdown-toggle::placeholder,
.small-select >>> .vs__selected-options .vs__selected {
  font-size: 0.8rem !important;
  line-height: 24px !important;
}

.small-select >>> .vs__search {
  padding: 0 !important;
  margin: 0 !important;
}

.small-select >>> .vs__placeholder {
  font-size: 0.8rem !important;
  line-height: 24px !important;
  padding: 0 !important;
  margin: 0 !important;
  color: #6e6b7b !important;
}

.small-select {
  flex: 1;
  max-width: 120px;
  min-width: 120px;
}

.working-group-popover {
  min-width: 300px !important;
  width: 1000px !important;
  max-width: 1000px !important;
}
</style>

<style>
.vs__dropdown-option--disabled {
  opacity: 0.6;
  cursor: not-allowed !important;
  background-color: #f8f9fa !important;
}

.report-menu-container {
  display: flex;
  flex-direction: column;
  align-items: end;
  justify-content: start;
  gap: 10px;
}
</style>

<style>
.drag-handle-cell {
  width: 30px !important;
  min-width: 30px !important;
  max-width: 30px !important;
  text-align: center;
  vertical-align: middle !important;
  padding: 0 !important;
  background-color: #f8f8f8;
  border-right: 1px solid #ebe9f1;
}

.drag-handle {
  color: #6e6b7b;
  cursor: move;
  padding: 10px 0;
}

.drag-handle:hover {
  color: #5e5873;
}

/* Add this to ensure proper table structure */
.table > tbody > tbody {
  border: none;
}

.table > tbody > tbody > tr {
  display: table-row;
}

.table > tbody > tbody > tr > td {
  display: table-cell;
}
</style>

<style>
.ghost-row {
  opacity: 0.5;
  background: #c8ebfb;
}

.drag-row {
  background: #eee;
}

.drag-handle {
  cursor: ns-resize;
}

.row-actions-drag-handle {
  cursor: ns-resize;
}
</style>

<style>
.vue-treeselect__limit-tip-text {
  padding-left: 5px;
}
</style>
